// ─────────────────────────────────────────────────────────────────────────────
// CANONICAL NC ACTION/STATE LABELS (E34-S21)
//
// SINGLE frontend source of truth for the human-readable label of every NC
// action/state. Every NC surface (dashboard card, bulk bar, action buttons,
// dialogs, calendar tooltips) references these constants — no surface defines
// its own copy of these strings. The backend mirrors these labels via
// `reports.maintenance_logs.action_types.*` (Reports::NonComplianceActionLabel);
// the shared contract is keyed on the ComplianceReviewStatus code (CNM/ODE/NPT)
// plus the derived Resolve action. Mapping documented in
// `docs/nc-terminology-glossary.md`. The rebuild uses "Resolve the Miss" /
// "Override the Miss" per product decision — NOT the legacy-verbatim strings.
// ─────────────────────────────────────────────────────────────────────────────
const ncActionLabels = {
  resolve: 'Resolve the Miss',
  noTesting: 'No Testing',
  override: 'Override the Miss',
  confirm: 'Non-Compliance',
  confirmFull: 'Confirm Non-Compliant Event',
  confirmed: 'Confirmed Non-Compliance',
  unreviewed: 'Non-Compliance (Unreviewed)',
  // REBUILD-461: explicit retire when the missed window is fully inside a closed offline span.
  offlineRetire: 'Clear Offline',
} as const

// E63-S1 lock 6: the grid symbol and the Monthly Symbol Guide must use identical wording.
const completeDateChangedLabel = 'Complete (date changed)' as const

export const en = {
  restoreDialog: {
    title: 'Restore',
    loading: 'Loading deactivated children...',
    descriptionWithChildren:
      'Select which deactivated items to restore along with this record. You can restore none, some, or all.',
    descriptionEmpty: 'This record has no deactivated children. Click Restore to proceed.',
    department: 'Department',
    unit: 'Unit',
    instrument: 'Instrument',
    tasksAutoRestored: 'Tasks are restored automatically.',
    selectAll: 'Select all',
    cancel: 'Cancel',
    confirm: 'Restore',
    parentRequired:
      'Select the parent to restore this item — a deactivated child cannot be restored while its parent stays deactivated.',
  },
  common: {
    // Comment indicator on a result row; shared by the Reports list and Maintenance Logs history.
    commentCountAria: 'Comments: {count}',
    exportFailed: 'Error while exporting. Please try again or contact system admin.',
    passwordInput: {
      showPassword: 'Show password',
      hidePassword: 'Hide password',
    },
    permissions: {
      noActionAccess: 'You do not have permission to perform this action.',
    },
    underConstruction: {
      title: 'Under construction',
      description: 'This page is being rebuilt and will be available soon.',
    },
    time: {
      hourLabel: 'Hour',
      minuteLabel: 'Minute',
      periodLabel: 'AM/PM',
      am: 'AM',
      pm: 'PM',
    },
    date: {
      monthLabel: 'Month',
      dayLabel: 'Day',
      yearLabel: 'Year',
      dateLabel: 'Date',
    },
    confidentialData: 'This is confidential data',
  },
  sessionTimeout: {
    title: 'Session Timeout!',
    line1: "You're being timed out due to inactivity.",
    line2: 'Please Choose to stay signed in or to logoff.',
    line3: 'Otherwise, You will be logged off automatically.',
    logOff: 'Log off',
    stayLoggedIn: 'Stay logged in ({seconds})',
  },
  nav: {
    appName: 'LabLogs',
    sites: 'Sites',
    departments: 'Departments',
    departmentsAndUnits: 'Departments & Units',
    units: 'Units',
    adminRoles: 'Role Management',
    users: 'Users',
    loggers: 'Loggers',
    people: 'People',
    catalog: 'Catalog',
    instrumentTypes: 'Instrument Types',
    instrumentTags: 'Tags',
    operationalCalendar: 'Operational calendar',
    codeValues: 'Code values',
    organization: 'Organization',
    instruments: 'Instruments',
    tasks: 'Tasks',
    tasksDashboard: 'Tasks Dashboard',
    nonCompliance: 'Non-Compliance',
    nonCompliancePending: 'Pending non-compliance review',
    tasksPending: 'Pending tasks',
    pendingApprovalsPending: 'Pending signature approvals',
    compliance: 'Compliance',
    complianceDashboard: 'Compliance Dashboard',
    dashboard: 'Dashboard',
    reports: 'Reports',
    monthlyReports: 'Monthly Reports',
    administration: 'Administration',
    complianceReviews: 'Compliance Reviews',
    deletedHistory: 'Deleted History',
    sitesReport: 'Sites Report',
    departmentsReport: 'Departments Report',
    unitsReport: 'Units Report',
    instrumentsReport: 'Instruments Report',
    maintenanceLogsReport: 'Maintenance Logs',
    usersReport: 'Users Report',
    pendingApprovals: 'Signatures',
    signOut: 'Sign out',
    openMenu: 'Open main menu',
    closeMenu: 'Close menu',
    mainMenuTitle: 'Main menu',
    mainMenuDescription: 'Application sections and account navigation',
    signedInAs: 'Signed in as',
    signoffMonitoring: 'Sign-off Monitoring',
    systemSettings: 'System settings',
    subscription: 'Subscription & capacity',
    myProfile: 'My Profile',
    notificationPreferences: 'Notification preferences',
    releaseNotes: 'Release {version}',
    needHelpContactUs: 'Need Help? Contact Us',
    lots: 'Supplies',
    scanBarcode: 'Scan Barcode',
    helpDocs: 'Help Docs',
    companySite: 'www.lablogs.co',
  },
  auth: {
    login: {
      title: 'Sign in to LabLogs',
      emailLabel: 'Email',
      passwordLabel: 'Password',
      submit: 'Sign in',
      submitting: 'Signing in…',
      forgotPassword: 'Forgot password?',
      ssoButton: 'Sign in with SSO',
      ssoDivider: 'or',
    },
    passwordPolicy: {
      title: 'Password requirements',
      policyLength: 'At least 8 characters',
      policyUppercase: 'At least one uppercase letter (A–Z)',
      policyLowercase: 'At least one lowercase letter (a–z)',
      policyNumber: 'At least one number (0–9)',
      policySymbol: 'At least one special character (! @ # $ % ^ & *)',
    },
    newPassword: {
      title: 'Set your password',
      emailLabel: 'Signing in as {email}',
      passwordLabel: 'New password',
      passwordConfirmationLabel: 'Confirm new password',
      submit: 'Continue',
      submitting: 'Saving…',
      backToSignIn: 'Back to sign in',
    },
    forgotPassword: {
      title: 'Forgot Password',
      emailLabel: 'Email address',
      submit: 'Send reset link',
      submitting: 'Sending…',
      hint: "Enter your email and we'll send you a password reset link.",
      backToLogin: 'Back to sign in',
    },
    resetPassword: {
      title: 'Reset Password',
      activationConfirmTitle: 'Confirm your email',
      activationConfirmHint:
        'Enter the 6-digit code from your invitation email. Next, you will set your password.',
      activationCodeLabel: 'Confirmation code',
      codeLabel: 'Reset code',
      passwordLabel: 'New password',
      confirmLabel: 'Confirm new password',
      continue: 'Continue',
      verifying: 'Verifying…',
      submit: 'Set new password',
      submitting: 'Saving…',
      backToLogin: 'Back to sign in',
    },
    customerSelector: {
      title: 'Select Organization',
      signedInAs: 'Signed in as {email}',
      searchLabel: 'Search',
      searchPlaceholder: 'Search organizations…',
      empty: 'No organizations match your search.',
      submit: 'Continue',
      cancel: 'Cancel',
    },
    footer: {
      copyright: 'Copyright © {year} LabLogs, Inc.',
      releaseLabel: 'Release {version}',
    },
  },
  errors: {
    notFound: {
      heading: 'Page not found',
      description:
        'The page you are looking for does not exist or may have been moved. Check the address or go back to the home page.',
    },
    forbidden: {
      heading: 'Access denied',
      description:
        'You do not have permission to view this page. If you believe this is a mistake, contact your administrator.',
    },
    backToHome: 'Back to home',
  },
  sso: {
    error: {
      title: 'Single sign-on unavailable',
      heading: 'We could not sign you in',
      description:
        'Single sign-on is not available for this organization, or the sign-in attempt could not be verified. Please contact your administrator or sign in with your password.',
      backToLogin: 'Back to sign in',
      reasons: {
        tenantNotFound:
          'Single sign-on is not available for this organization. Check your link or contact your administrator.',
        notConfigured:
          'Single sign-on has not been set up for this organization. Please sign in with your password or contact your administrator.',
        authFailed:
          'We could not verify your sign-in attempt. Please try again or sign in with your password.',
      },
    },
  },
  customerSwitcher: {
    switchOrganization: 'Switch organization',
    current: 'Current',
    ariaOpen: 'Switch organization, open menu',
    searchLabel: 'Search organizations',
    searchPlaceholder: 'Search organizations…',
    empty: 'No organizations match your search.',
  },
  organization: {
    sites: {
      title: 'Sites',
      newSite: 'New Site',
      siteLimitReached:
        'Site creation limit reached. Please contact your administrator or LabLogs Support to increase your site limit.',
      edit: 'Edit',
      searchPlaceholder: 'Search by name, city, address, or country…',
      summarySeparator: ' · ',
      searchResultsCount: '{count} sites found',
      searchNoResults: 'No results found',
      searchClearAria: 'Clear search',
      paginationPage: 'Page',
      paginationOf: 'of',
      paginationNoun: 'sites',
      summaryDepartmentsUnits: '{departmentsCount} departments · {unitsCount} units',
      summaryDepartmentsOnly: '{count} departments',
      summaryUnitsOnly: '{count} units',
      table: {
        id: 'ID',
        name: 'Name',
        address: 'Address',
        timezone: 'Timezone',
        country: 'Country',
        departments: 'Departments',
        units: 'Units',
        complianceScore: 'Compliance Score',
        actions: 'Actions',
      },
      filterByCompliance: 'Filter by compliance',
      allCompliance: 'Compliance: All',
      complianceLow: 'Compliance: < 80%',
      complianceMedium: 'Compliance: 80–89%',
      complianceHigh: 'Compliance: ≥ 90%',
      complianceNone: 'Compliance: No data',
      form: {
        name: 'Site Name',
        namePlaceholder: 'e.g. Main Campus',
        nameRequired: 'Site name is required.',
        address: 'Address',
        addressPlaceholder: 'e.g. 123 Lab Street',
        addressRequired: 'Address is required.',
        city: 'City',
        cityPlaceholder: 'e.g. Boston',
        cityRequired: 'City is required.',
        state: 'State',
        statePlaceholder: 'e.g. MA',
        stateRequired: 'State is required.',
        zip: 'Zip',
        zipPlaceholder: 'e.g. 02101',
        zipRequired: 'Zip is required.',
        phoneNumber: 'Phone Number',
        phoneNumberPlaceholder: 'e.g. 617 555 0100',
        phoneNumberRequired: 'Phone number is required.',
        phoneNumberInvalidForCode: 'Enter a valid phone number for the selected phone code.',
        phoneCode: 'Phone Code',
        phoneCodeRequired: 'Phone code is required.',
        timezone: 'Timezone',
        timezoneRequired: 'Timezone is required.',
        country: 'Country',
        countryRequired: 'Country is required.',
        followParentSchedule: 'Follow Parent Schedule',
        submit: 'Save Site',
        submitting: 'Saving…',
        cancel: 'Cancel',
      },
      empty: 'No sites found.',
      previous: 'Previous',
      next: 'Next',
      delete: 'Disable',
      deleteConfirmTitle: 'Disable Site',
      deleteConfirmBody:
        'This site has {departmentsCount} departments, {unitsCount} units, and {instrumentsCount} instruments. All will be deactivated.',
      deleteConfirmBodyEmpty: 'Are you sure you want to disable this site?',
      deleteConfirmAction: 'Disable Site',
      deleteError: 'Failed to load disable preview.',
      clearFilters: 'Clear',
      showDeleted: 'Show disabled',
      hideDeleted: 'Hide disabled',
      deleted: 'Disabled',
      restore: 'Restore',
    },
    departments: {
      title: 'Departments',
      newDepartment: 'New Department',
      edit: 'Edit',
      editTitle: 'Edit Department',
      signoffChain: 'Sign-off Chain',
      searchPlaceholder: 'Search by name…',
      searchClearAria: 'Clear search',
      filterBySite: 'Filter by site',
      allSites: 'All sites',
      filterByCompliance: 'Filter by compliance',
      allCompliance: 'Compliance: All',
      complianceLow: 'Compliance: < 80%',
      complianceMedium: 'Compliance: 80–89%',
      complianceHigh: 'Compliance: ≥ 90%',
      complianceNone: 'Compliance: No data',
      table: {
        id: 'ID',
        name: 'Name',
        site: 'Site',
        units: 'Units',
        complianceScore: 'Compliance Score',
        actions: 'Actions',
      },
      form: {
        name: 'Department Name',
        namePlaceholder: 'e.g. Microbiology Lab',
        nameRequired: 'Department name is required.',
        site: 'Site',

        siteRequired: 'Site is required.',
        submit: 'Save Department',
        submitting: 'Saving…',
        cancel: 'Cancel',
        unitsSectionTitle: 'Benches / Units: ({count})',
        unitLabel: '{number}. Bench / Unit',
        addUnit: 'Add Bench / Unit',
        removeUnit: 'Remove',
        removeUnitConfirm: 'Are you sure you want to remove this unit?',
        removeUnitWithInstruments:
          'This unit has linked instruments. Removing it will deactivate the unit and its instruments. Continue?',
        removeUnitTitle: 'Remove Unit',
        removeUnitAction: 'Remove',
      },
      delete: 'Disable',
      deleteConfirmTitle: 'Disable Department',
      deleteConfirmBody:
        'This department has {unitsCount} units and {instrumentsCount} instruments. All will be deactivated.',
      deleteConfirmBodyEmpty: 'Are you sure you want to disable this department?',
      deleteConfirmAction: 'Disable Department',
      deleteError: 'Failed to load disable preview.',
      clearFilters: 'Clear',
      showDeleted: 'Show disabled',
      hideDeleted: 'Hide disabled',
      deleted: 'Disabled',
      restore: 'Restore',
      restoreBlockedSiteDeleted:
        'The associated site has been disabled. Please restore it and try again.',
      empty: 'No departments found.',
      paginationPage: 'Page',
      paginationOf: 'of',
      paginationNoun: 'departments',
      previous: 'Previous',
      next: 'Next',
    },
    units: {
      title: 'Units',
      newUnit: 'New Unit',
      newBatchUnits: 'Create multiple units',
      batchCreate: {
        title: 'Create Multiple Units',
        sharedSection: 'Shared License & Accreditation',
        unitRowsSection: 'Units',
        unitLabel: 'Bench / Unit Name {number}',
        addAnotherUnit: 'Add another unit',
        removeUnit: 'Remove',
        submit: 'Create Units',
        submitting: 'Creating…',
      },
      edit: 'Edit',
      editTitle: 'Edit Unit',
      searchPlaceholder: 'Search by name…',
      searchClearAria: 'Clear search',
      filterBySite: 'Filter by site',
      allSites: 'All sites',
      filterByDepartment: 'Filter by department',
      allDepartments: 'All departments',
      table: {
        name: 'Name',
        department: 'Department',
        site: 'Site',
        licenseNumber: 'License Number',
        actions: 'Actions',
      },
      form: {
        name: 'Bench / Unit Name',
        namePlaceholder: 'e.g. Hematology Unit',
        nameRequired: 'Bench / Unit Name is required.',
        department: 'Department',
        selectDepartment: 'Select department',
        departmentRequired: 'Department is required',
        licenseNumber: 'CLIA License Number',
        licenseNumberPlaceholder: 'e.g. LAB-2024-001',
        licenseOrAccreditationRequired: 'Please enter at least one license.',
        followParentSchedule: 'Follow Parent Schedule',
        accreditationSection: 'Accreditation',
        submit: 'Save Unit',
        submitting: 'Saving…',
        cancel: 'Cancel',
      },
      delete: 'Disable',
      deleteConfirmTitle: 'Disable Unit',
      deleteConfirmBody: 'This unit has {instrumentsCount} instruments. All will be deactivated.',
      deleteConfirmBodyEmpty: 'Are you sure you want to disable this unit?',
      deleteConfirmAction: 'Disable Unit',
      deleteError: 'Failed to load disable preview.',
      clearFilters: 'Clear',
      showDeleted: 'Show disabled',
      hideDeleted: 'Hide disabled',
      deleted: 'Disabled',
      restore: 'Restore',
      restoreBlockedDepartmentDeleted:
        'The associated department has been disabled. Please restore it and try again.',
      empty: 'No units found.',
      paginationPage: 'Page',
      paginationOf: 'of',
      paginationNoun: 'units',
      previous: 'Previous',
      next: 'Next',
    },
    hierarchy: {
      title: 'Organization Hierarchy',
      subtitle: 'Full structure: sites, departments, and units',
      noDepartments: 'No departments',
      noUnits: 'No units',
      unassigned: 'Unassigned Units',
      licenseNumber: 'License',
      manageSites: 'Manage sites',
      manageDepartments: 'Manage departments',
      manageUnits: 'Manage units',
      departmentsSummary: '{count} departments',
      unitsSummary: '{count} units',
    },
  },
  admin: {
    subscription: {
      title: 'Subscription & capacity',
      subtitle:
        'Read-only usage for your organization. User and site limits match the checks used when people or sites are created.',
      usersTitle: 'User seats',
      sitesTitle: 'Sites',
      inUse: 'In use',
      allowedUsers: 'Allowed',
      allowedSites: 'Site cap',
      remaining: 'Remaining',
      unlimited: 'Unlimited',
      dash: '—',
      userLimitZeroNote: 'A limit of 0 means no additional non-lablog users for this organization.',
      utilizationLabel: 'Utilization',
    },
    users: {
      title: 'User Directory',
      searchPlaceholder: 'Search by name, email, or ID…',
      searchButton: 'Search',
      filterSite: 'Filter by site',
      allRoles: 'All roles',
      allSites: 'All sites',
      activeOnly: 'Active only',
      clearFilters: 'Clear',
      roleUser: 'User',
      roleUserPlus: 'Supervisor',
      roleAdmin: 'Admin',
      paginationPage: 'Page',
      paginationOf: 'of',
      paginationNoun: 'users',
      table: {
        name: 'Name',
        llId: 'LL ID',
        employeeId: 'Employee ID',
        role: 'Role',
        jobTitle: 'Job Title',
        lastLogon: 'Last Logon',
        status: 'Status',
        actions: 'Actions',
        empty: 'No users found.',
      },
      editButton: 'Edit',
      manageAccessButton: 'Manage Access',
      notificationPreferencesButton: 'Notification Preferences',
      notificationPreferences: {
        pageTitle: 'Notification Preferences — {name}',
        pageSubtitle: 'Manage which alerts this user receives, and how.',
        backToUser: '← Back to user',
      },
      resendAccessButton: 'Resend Access Email',
      resendPasswordButton: 'Resend Password Email',
      newButton: 'New User',
      userLimitReached:
        'User account limit reached. Please contact your administrator or LabLogs Support to increase your user limit.',
      statusActive: 'Active',
      statusInactive: 'Disabled',
      previous: 'Previous',
      next: 'Next',
      show: {
        backToDirectory: 'Back to Directory',
        profile: 'Profile',
        sites: 'Sites',
        departments: 'Departments',
        units: 'Units',
        neverLoggedIn: 'Never logged in',
        yes: 'Yes',
        no: 'No',
      },
      access: {
        title: 'Manage Access',
        sitesSection: 'Sites',
        sitesSectionCount: 'Sites ({count})',
        departmentsSection: 'Departments',
        departmentsSectionCount: 'Departments ({count})',
        unitsSection: 'Units',
        unitsSectionCount: 'Units ({count})',
        saveButton: 'Save Access',
        backToProfile: '← Back to Profile',
        noSites: 'No sites available.',
        noDepartments: 'No departments available.',
        noUnits: 'No units available.',
        notApplicable: 'Location access not applicable',
        notApplicableDescription:
          'Location scoping only applies to Customer roles. This user has unrestricted access by default.',
        searchPlaceholder: 'Search...',
        selectAll: 'Select all',
        deselectAll: 'Deselect all',
        expandAll: 'Expand all',
        collapseAll: 'Collapse all',
        selectedSummary: '{sites} sites, {departments} departments, {units} units selected',
        unassignedDepartments: 'Unassigned departments',
        unassignedUnits: 'Unassigned units',
      },
      form: {
        newTitle: 'New User',
        editTitle: 'Edit User',
        firstNameLabel: 'First Name',
        lastNameLabel: 'Last Name',
        middleNameLabel: 'Middle Name',
        emailLabel: 'Email Address',
        phoneLabel: 'Phone',
        phoneCodeLabel: 'Phone Code',
        jobTitleLabel: 'Job Title',
        employeeIdLabel: 'Employee ID',
        federatedUserIdLabel: 'Federated User ID',
        roleLabel: 'Role',
        noRole: 'No role',
        rolePlaceholder: 'Select a role',
        roleGroupLablogs: 'Lab Logs',
        roleGroupCustomer: 'Customer',
        roleGroupReseller: 'Reseller',
        roleLablogs_admin: 'LabLogs Admin',
        roleLablogs_supervisor: 'LabLogs Supervisor',
        roleLablogs_user: 'LabLogs User',
        roleCustomer_admin: 'Admin',
        roleCustomer_supervisor: 'Supervisor',
        roleCustomer_user: 'User',
        roleReseller_admin: 'Reseller Admin',
        roleReseller_supervisor: 'Reseller Supervisor',
        roleReseller_user: 'Reseller User',
        viewOnlyLabel: 'View only',
        viewOnlyDescription: 'Restricts this user to read-only access across all resources.',
        canViewConfidentialLabel: 'Can view confidential information',
        accountActiveLabel: 'Account active',
        accountDisabledLabel: 'Account disabled',
        disableButton: 'Disable User',
        enableButton: 'Enable User',
        disableModalTitle: 'Are you sure you want to disable this user?',
        disableModalBody:
          'They will no longer be able to logon but all recorded data will be saved.',
        enableModalTitle: 'Are you sure you want to enable this user?',
        enableModalBody: 'They will be able to logon application.',
        modalConfirm: 'Yes',
        modalCancel: 'No',
        pendingPasswordSetupHint:
          'This user has not set a password yet. Resend the activation email with a setup link and code.',
        resendPasswordHint:
          'Send a password reset email with a new code. The user can set a new password via Forgot Password or the link in the message.',
        submitCreate: 'Create User',
        submitUpdate: 'Save Changes',
        cancelButton: 'Cancel',
      },
    },
    settings: {
      pageTitle: 'System settings',
      backToSubscription: 'Back to subscription & capacity',
      pageDescription:
        'Organization name and code are for reference and cannot be edited on this page. The profile name in the header (e.g. “Admin”) is the person’s name, not a role. Editable options are in Security and Identity provider — use Save. Saving requires the Administrator account role in this organization.',
      readOnlyNote:
        'You can read this page, but your current role in this organization cannot save changes. Sign in as a user with the Administrator role, or ask one to change settings for you.',
      pageDescriptionViewOnly:
        'This page is read-only for your account. Below are the current values — no input fields on purpose.',
      generalNameCodeReadOnly: 'Shown for reference. Name and code are not changed here.',
      emptyValue: '—',
      saveButton: 'Save changes',
      sectionGeneral: 'General',
      sectionSecurity: 'Security & limits',
      sectionKeyValue: 'Configuration values',
      tenantNameLabel: 'Organization name',
      tenantCodeLabel: 'Organization code',
      sessionTimeoutLabel: 'Session timeout (minutes)',
      sessionTimeoutManagedNote:
        'Managed by LabLogs. Contact your provider to change this setting.',
      timeFormatLabel: 'Time format',
      timeFormat12: '12-hour',
      timeFormat24: '24-hour',
      idpSignInLabel: 'IDP sign-in URL',
      idpSignOutLabel: 'IDP sign-out URL',
      idpUrlPlaceholder: 'https://',
      userLimitInvalid: 'Enter a whole number, or clear the field to use 0.',
      sectionOperations: 'Operations & scheduling',
      operationsIntro:
        'Operational toggles carried over from legacy system settings (instrument logging and supply access).',
      operationsTimeFormatReminderLine:
        'Scheduling pages show datetimes using this organization’s format:',
      operationsTimeFormatReminderHint: 'Change it under Security & limits above.',
      enableLotAccessLabel: 'Supply access (supply checks)',
      enableLotAccessHelp:
        'When enabled, behavior follows legacy “enable supply access”. Turning this off does not remove existing supply records.',
      enableLoggingLabel: 'Enable instrument logging',
      enableLoggingHelp:
        'Require a logging step when instrument tasks are completed. When off, tasks are recorded without a separate logging control.',
      instrumentSecurityLabel: 'Instrument Security',
      instrumentSecurityDefaultLabel: 'Default logging mode',
      instrumentSecurityDefaultHelp: 'Choose how a performer is recorded when a task is logged.',
      loggingModeOffLabel: 'Off',
      loggingModeOlLabel: 'Open Logging (no password)',
      loggingModePrLabel: 'Authenticated Users Only',

      booleanYes: 'Yes',
      booleanNo: 'No',
    },
    loggers: {
      pageTitle: 'Logger Management',
      newButton: 'New Logger',
      editButton: 'Edit',
      manageButton: 'Manage Loggers',
      searchPlaceholder: 'Search by name…',
      onlyActiveLabel: 'Active only',
      clearFilters: 'Clear',
      form: {
        firstName: 'First Name',
        lastName: 'Last Name',
        middleName: 'Middle Name',
        jobTitle: 'Job Title',
        employeeId: 'Employee ID',
        activeLabel: 'Active',
        submitCreate: 'Create Logger',
        submitUpdate: 'Update Logger',
        cancel: 'Cancel',
      },
      table: {
        name: 'Name',
        jobTitle: 'Job Title',
        employeeId: 'Employee ID',
        status: 'Status',
        active: 'Active',
        inactive: 'Inactive',
        actions: 'Actions',
        empty: 'No loggers found.',
      },
      paginationNoun: 'loggers',
      searchButton: 'Search',
      editTitle: 'Edit Logger',
    },
    instrumentTypes: {
      pageTitle: 'Instrument Types',
      newButton: 'New Instrument Type',
      editButton: 'Edit',
      cloneButton: 'Clone',
      cloneDialog: {
        title: 'Clone Instrument Type',
        description:
          'This creates a full copy of this instrument type, including its supplies and maintenance tasks. There is no way to fully delete an instrument type afterward, so please confirm the name before continuing.',
        nameLabel: 'New instrument type name',
        cancel: 'Cancel',
        confirm: 'Clone',
      },
      newTitle: 'New Instrument Type',
      editTitle: 'Edit Instrument Type',
      backToList: '← Back to instrument types',
      nameField: 'Name',
      namePlaceholder: 'e.g. Centrifuge, PCR Thermocycler, Spectrophotometer',
      descriptionField: 'Description',
      compliancePercentField: 'Rolling Compliance %',
      manufacturerField: 'Manufacturer',
      manufacturerModelField: 'Manufacturer Model',
      noManufacturer: 'No manufacturer',
      noModel: 'No model',
      attachmentsColumn: 'Attachments',
      attachmentsCount: 'Attachments: {count}',
      attachments: {
        dialogTitle: 'Attachments — {name}',
        sectionTitle: 'Attachments',
        empty: 'No documents attached to this instrument type yet.',
        loadFailed: 'Failed to load attachments. Please try again.',
        uploadFailed: 'Upload failed. Please check the file and try again.',
        deleteFailed: 'Delete failed. Please try again.',
        fileUpload: 'File upload',
        chooseFile: 'Choose file',
        orDragDrop: 'or drag and drop',
        allowedTypes: 'PDF, Word, Excel, PNG, JPG and more',
        descriptionLabel: 'File description (optional)',
        descriptionPlaceholder: 'Optional',
        uploading: 'Uploading…',
        upload: 'Upload',
        addFile: 'Add file',
        downloadSr: 'Download',
        deleteSr: 'Delete',
        undoDeleteSr: 'Undo delete',
        clearFile: 'Clear file',
        removePendingSr: 'Remove from queue',
      },
      activeColumn: 'Active',
      submitCreate: 'Create Type',
      submitUpdate: 'Save Changes',
      cancelButton: 'Cancel',
      created: 'Instrument type created successfully.',
      updated: 'Instrument type updated successfully.',
      searchButton: 'Search',
      searchPlaceholder: 'Search instrument types...',
      filterSite: 'Site',
      filterDepartment: 'Department',
      filterUnit: 'Unit',
      filterTags: 'Tags',
      allSites: 'All sites',
      allDepartments: 'All departments',
      allUnits: 'All units',
      tagsPlaceholder: 'All tags',
      inactiveLabel: 'Inactive',
      emptyState: 'No instrument types found.',
      withoutInstrumentsHeader: '({count}) Instrument types without instruments',
      withInstrumentsHeader: '({count}) Instrument types with ({total}) instruments',
      instrumentsCount: 'Instruments: {count}',
      sitesLabel: 'Sites:',
      instrumentList: {
        toggleShow: 'Show instruments',
        toggleHide: 'Hide instruments',
        loading: 'Loading instruments…',
        empty: 'No instruments added.',
        site: 'Site',
        department: 'Department',
        unit: 'Bench / Unit',
        ellId: 'ELL ID',
        serialNumber: 'S/N',
        status: 'Status',
        noStatus: 'No status',
        viewInstrument: 'View Instrument',
        viewMaintenanceLogs: 'View Maintenance Logs',
        controlsReagents: 'Supplies',
        addInstrument: 'Add Instrument',
        editButton: 'Edit',
        cloneButton: 'Clone',
      },
      complianceScore: 'Compliance Score',
      clearFilters: 'Clear',
      filterByCompliance: 'Filter by compliance',
      allCompliance: 'Compliance: All',
      complianceLow: 'Compliance: < 80%',
      complianceMedium: 'Compliance: 80–89%',
      complianceHigh: 'Compliance: ≥ 90%',
      complianceNone: 'Compliance: No data',
      wizardTitle: 'Add Instrument Type',
      wizardNext: 'Next',
      wizardStepOneLabel: 'Step 1 of 2 · Identity',
      wizardStepTwoLabel: 'Step 2 of 2 · Planning',
      manufacturerTextField: 'Manufacturer',
      manufacturerTextPlaceholder: 'Manufacturer name (optional)',
      modelNumberTextField: 'Model #',
      modelNumberTextPlaceholder: 'Model number (optional)',
      // REBUILD-39: legacy cancel confirmation before discarding in-memory create draft
      cancelCreateDialog: {
        title: 'Confirmation',
        body: 'Are you sure you want to cancel adding Instrument Type? You will lose all changes.',
        confirm: 'Yes',
        cancel: 'Cancel',
      },
      planning: {
        pageTitle: 'Planning',
        leadText:
          'Confirm instrument identity and add planning rows for supplies and maintenance tasks.',
        identityTitle: 'Instrument details',
        editButton: 'Edit',
        removeButton: 'Remove',
        removeConfirm:
          'Remove this task from the type? Only future scheduling will stop. All historical completions and non-compliances are preserved.',
        removeConfirmTitle: 'Remove this task?',
        removeSupplierConfirmTitle: 'Remove this Supplier?',
        removeConfirmButton: 'Remove',
        removeCancelButton: 'Cancel',
        removeConfirmNeverCompleted: 'Remove this task from the type?',
        removeReagentControlConfirm: 'Are you sure you want to remove it?',
        saveButton: 'Save',
        cancelButton: 'Cancel',
        noneLabel: 'None',
        controlsSectionTitle: 'Supplies',
        linkedToTask: 'Linked to Task',
        maintenanceSectionTitle: 'Maintenance Tasks',
        taskNameLabel: 'Name',
        addButton: 'Add',
        rowActions: 'Actions',
        finishButton: 'Finish',
        finishing: 'Saving…',
        finishError:
          'Could not finish creating this instrument type. Fix the errors and try again.',
        finishConfirmTitle: 'Confirm finish',
        finishConfirmIntro: 'You are finishing setup without the following items:',
        finishConfirmButton: 'Finish anyway',
        omissionControls: 'No supply requirements added.',
        omissionMaintenance: 'No maintenance tasks added.',
        addControlsReagents: 'Add Supplies',
        addMaintenanceTask: 'Add Maintenance Task',
        taskFrequencyTypeLabel: 'Frequency Type',
        taskFrequencyLabel: 'Frequency',
        taskStartAtLabel: 'Start At',
        taskEndsAtMidnightLabel: 'Ends At 23:59:59',
        taskNotesLabel: 'Notes',
        taskAffectsComplianceLabel: 'Affects Compliance',
        taskDataFieldsLabel: 'Data Fields',
        taskTagsLabel: 'Tags',
        taskSaveTagsButton: 'Save tags',
        dragHandleLabel: 'Drag to reorder',
        yes: 'Yes',
        no: 'No',
      },
      controlsModal: {
        addTitle: 'Add Supplies',
        editTitle: 'Edit Supplies',
        nameLabel: 'Name',
        namePlaceholder: 'Control 1',
        requiresValidation: 'Requires Validation',
        isKit: 'Is a Kit',
        isKitTooltip:
          'Mark this requirement as a kit when it bundles secondary components. A kit can hold one or more named secondaries.',
        isKitInfoLabel: 'More information about kits',
        secondariesTitle: 'Secondary components',
        secondaryName: 'Name',
        secondaryNamePlaceholder: 'Component name',
        secondaryNameRequired: 'Name is required.',
        secondaryDescription: 'Description',
        secondaryDescriptionPlaceholder: 'Description (optional)',
        secondaryDelete: 'Delete',
        addSecondary: 'Add a Secondary',
        kitRequiresSecondary: 'Is a kit requires at least one secondary component.',
        secondariesHiddenNote:
          'Unchecking "Is a Kit" hides secondary components; they are removed when you save.',
        notes: 'Notes',
        notesPlaceholder: 'Enter notes here…',
        useExistingLabel: 'Use an existing catalog supply',
        useExistingPlaceholder: 'Select catalog supply',
        useExistingNone: 'Create a new requirement',
        save: 'Save',
        cancel: 'Cancel',
        editAction: 'Edit',
      },
      maintenanceModal: {
        addTitle: 'Add Maintenance Task',
        editTitle: 'Edit Maintenance Task',
        cloneTitle: 'Clone Maintenance Task',
        nameLabel: 'Maintenance Task',
        namePlaceholder: 'Enter Maintenance Task',
        nameExampleTooltip: 'Example: Run Level 1 QC',
        nameInfoLabel: 'Maintenance task name example',
        notesLabel: 'Notes',
        notesPlaceholder: 'Enter notes here…',
        affectsCompliance: 'Affects Compliance',
        frequencyTypeLabel: 'Frequency Type',
        frequencyTypePlaceholder: 'Select a frequency type',
        frequencyTypeTimed: 'Timed',
        frequencyTypeCalendarFixed: 'Calendar (Ex Fixed)',
        frequencyTypeUnscheduled: 'Unscheduled',
        frequencyTypeTimedTooltip: 'The next execution depends on the previous one',
        frequencyTypeCalendarFixedTooltip: 'Executions do not depend on previous ones',
        frequencyLabel: 'Frequency',
        frequencySearchPlaceholder: 'Search frequencies…',
        frequencyNewOption: 'New frequency…',
        frequencySelectTypeFirst: 'Select a frequency type first',
        frequencySelectPlaceholder: 'Select a frequency…',
        frequencyEmpty: 'No matching frequencies',
        intervalOptionsTitle: 'Interval Options',
        intervalNameLabel: 'Name',
        intervalNamePlaceholder: 'Frequency name',
        intervalNameRequired: 'Frequency name is required.',
        intervalNameDuplicate: 'A frequency with this name already exists. Choose a unique name.',
        intervalOptionPreview: 'Option preview',
        repeatsEveryRequired: 'Repeats Every must be a positive number.',
        startsAtRequired: 'Starts At is required.',
        overdueRequired: 'Overdue must be a positive number.',
        overdueMinimum: 'Overdue must be at least 10 minutes.',
        overdueMaximum: 'Value must be less than or equal to {max}',
        overdueDurationTooLong: 'Overdue is too long for the selected unit. Use a smaller value.',
        repeatsEveryLabel: 'Repeats Every',
        startsAtLabel: 'Starts At',
        leadTimeLabel: 'Lead Time',
        overdueLabel: 'Overdue',
        endsAtMidnightLabel: 'Ends at 23:59:59',
        endsAtMidnightWarning: 'Not recommended: overdue window exceeds natural day boundary.',
        noRecurrence: 'No recurrence rule',
        unscheduledNote: 'Unscheduled — no recurrence rule',
        leadExceedsRepeatsWarning: 'Lead Time exceeds Repeats Every.',
        overdueExceedsRepeatsWarning: 'Overdue Time exceeds Repeats Every.',
        frequencyRequiredHint:
          'Select a frequency or a fixed schedule — a maintenance task must be scheduled.',
        selectUnit: 'Unit',
        taskLinksLabel: 'Task Links to Supplies',
        taskLinksNote:
          'Linking a Task to a Supply prompts the user to update the Acceptable Values whenever the Supply changes.',
        taskLinksNone: 'No linked supply',
        taskUsesLotsLabel: 'Task Uses Supplies',
        taskUsesLotsNote:
          'If a Task Uses a Supply, the user is required to specify the Supply before completing the Task.',
        taskUsesLotsEmpty: 'No supplies available for this type.',
        dataFieldsTitle: 'Data Fields',
        addDataField: 'Add Data Field',
        cloneDataField: 'Clone',
        removeDataField: 'Remove',
        required: 'Required',
        isConfidential: 'Is Confidential',
        fieldLabelLabel: 'Field Label',
        fieldLabelPlaceholder: 'Example: pH Level',
        fieldLabelRequired: 'Field Label is required to save this data field.',
        fieldDescriptionLabel: 'Description (optional)',
        fieldDescriptionPlaceholder: 'e.g. Record the result in °C',
        fieldTypeLabel: 'Field Type',
        fieldTypePlaceholder: 'Select a type',
        fieldTypeRequired: 'Field Type is required.',
        validateFieldTitle: 'Validate Field',
        acceptableValuesFromLot: '(Acceptable Values defined when a Supply is entered)',
        validateFieldAddRow: 'Add More Validation',
        validateFieldOperatorOr: 'OR',
        validateFieldOperatorAnd: 'AND',
        validateFieldValue: 'Value',
        validateFieldBetweenMin: 'Min',
        validateFieldBetweenMax: 'Max',
        validateFieldBetweenInvalid:
          "Please enter valid 'between' values (min must not exceed max).",
        validateFieldContradictory:
          'These acceptable conditions contradict each other and can never all be met. Remove or adjust them so a value can satisfy every rule.',
        validateFieldValidationType: 'Acceptable if',
        validateFieldAcceptableIf: 'Acceptable if',
        validateFieldIsAcceptable: 'Acceptable',
        validateFieldRemoveRow: 'Remove',
        definedValuesTitle: 'Defined Values',
        definedValueAddRow: 'Add More Validation',
        definedValuePlaceholder: 'Value',
        definedValueRemove: 'Remove',
        definedIsAcceptable: 'Acceptable',
        definedAcceptableRequired: 'At least one Defined Value must be marked as Acceptable.',
        save: 'Save',
        cancel: 'Cancel',
        editAction: 'Edit',
        updateAction: 'Update',
        cloneAction: 'Clone',
        nonComplianceLockTitle: 'Warning',
        nonComplianceLockBody:
          'There are currently {count} Non-Compliant event(s) in {departments}. This Maintenance Task cannot be changed until all Non-Compliance is reviewed for this Instrument Type.',
        nonComplianceLockConfirm: 'OK',
      },
      paginationPage: 'Page',
      paginationOf: 'of',
      paginationNoun: 'instrument types',
      image: {
        sectionTitle: 'Type Image',
        currentImage: 'Current image',
        noImage: 'No image uploaded yet.',
        uploadButton: 'Upload Image',
        changeButton: 'Change Image',
        allowedTypes:
          'Accepted formats: JPEG, PNG, GIF, TIFF. Max 20 MB. Dimensions up to 400 × 400 px.',
        dimensionsTooLarge: 'Image dimensions must not exceed 400 × 400 px.',
      },
    },
    schedules: {
      pageTitle: 'Operational calendar',
      saveWorkWeek: 'Save',
      readOnlyWorkWeek:
        'View only: your role can view the calendar but an administrator is required to make changes.',
      systemSchedule: {
        title: 'Default System Schedule',
      },
      customDaysOff: {
        title: 'Custom Days Off',
        addDate: '+ Add Date',
        remove: 'Remove',
        removeConfirm: 'Remove this custom day off? This cannot be undone.',
        cancel: 'Cancel',
        emptyState: 'No custom days off are configured yet.',
        duplicateDate: 'A custom day off is already set for that date.',
      },
    },
    codeValues: {
      pageTitle: 'Code values',
      newButton: 'New code value',
      editButton: 'Edit',
      deleteButton: 'Delete',
      deleteConfirm: 'Delete this code value? This cannot be undone if nothing references it.',
      newTitle: 'New code value',
      editTitle: 'Edit code value',
      backToList: '← Back to code values',
      categoryPlaceholder: 'Select category',
      categoryNone: 'None',
      categoryManualSlug: 'Enter category slug manually',
      categoryPickFromList: 'Choose from list',
      categorySlugPlaceholder: 'e.g. task_status',
      fields: {
        code: 'Code',
        description: 'Description',
        category: 'Category',
        sortOrder: 'Sort order',
      },
      columns: {
        code: 'Code',
        description: 'Description',
        category: 'Category',
        sortOrder: 'Sort',
        actions: 'Actions',
      },
      categoryFilter: 'Category',
      allCategories: 'All categories',
      applyFilter: 'Apply',
      submitCreate: 'Create',
      submitUpdate: 'Save changes',
      cancelButton: 'Cancel',
      emptyState: 'No code values found.',
      paginationPage: 'Page',
      paginationOf: 'of',
      paginationNoun: 'rows',
      previous: 'Previous',
      next: 'Next',
    },
    signoffMonitoring: {
      pageTitle: 'Sign-off Monitoring',
      stats: {
        pendingSignoffs: 'Pending sign-offs',
        missingChains: 'Missing chains',
      },
      pendingSignoffs: {
        sectionTitle: 'Pending sign-offs by department',
        columns: {
          department: 'Department',
          pendingCount: 'Pending',
          oldestDate: 'Oldest pending',
        },
        emptyState: 'No pending sign-off records.',
        viewDashboard: 'View',
      },
      chainStatus: {
        sectionTitle: 'Sign-off chain status',
        columns: {
          department: 'Department',
          chainStatus: 'Chain status',
          actions: 'Actions',
        },
        configured: 'Configured',
        notConfigured: 'Not configured',
        configureLink: 'Configure chain',
        emptyState: 'No departments found.',
      },
    },
    instrumentTags: {
      pageTitle: 'Tags',
      newButton: 'New Tag',
      newTitle: 'New Tag',
      backToList: '← Back to tags',
      nameField: 'Tag Name',
      nameRequired: 'Name is required',
      instrumentsColumn: 'Instruments',
      activeColumn: 'Active',
      activeLabel: 'Active',
      inactiveLabel: 'Inactive',
      deactivateButton: 'Deactivate',
      deactivateConfirm:
        'Deactivate this tag? It will no longer appear on instruments, tasks, or tag filters. It will show again on its current assignments if you activate it.',
      activateButton: 'Activate',
      activateConfirm:
        'Activate this tag? It will appear again on the instruments and tasks it is still assigned to.',
      submitCreate: 'Create Tag',
      submitUpdate: 'Save Changes',
      editTitle: 'Edit Tag',
      editButton: 'Edit',
      cancelButton: 'Cancel',
      created: 'Tag created successfully.',
      updated: 'Tag updated successfully.',
      discarded: 'Tag deleted.',
      searchPlaceholder: 'Search by name…',
      searchButton: 'Search',
      clearFilters: 'Clear',
      stateFilter: {
        all: 'All',
        active: 'Active',
        inactive: 'Inactive',
      },
      emptyState: 'No tags found.',
      paginationPage: 'Page',
      paginationOf: 'of',
      paginationNoun: 'tags',
      previous: 'Previous',
      next: 'Next',
    },
    roles: {
      title: 'Role Management',
      searchPlaceholder: 'Search by name or email…',
      table: {
        name: 'Name',
        email: 'Email',
        role: 'Role',
        actions: 'Actions',
        noRole: 'No role assigned',
      },
      assignRole: 'Assign Role',
      removeRole: 'Remove Role',
      removeConfirmTitle: 'Remove Role',
      removeConfirm: "Are you sure you want to remove this user's role?",
      cancel: 'Cancel',
      confirm: 'Remove',
      previous: 'Previous',
      next: 'Next',
    },
  },
  instruments: {
    title: 'Instruments',
    newButton: 'New Instrument',
    searchPlaceholder: 'Search by label or serial…',
    searchButton: 'Search',
    filtersTitle: 'Filters',
    filtersDone: 'Done',
    filtersClear: 'Clear',
    allSites: 'All sites',
    allDepartments: 'All departments',
    allUnits: 'All units',
    filterSearch: 'Search',
    filterSite: 'Site',
    filterDepartment: 'Department',
    filterUnit: 'Unit',
    filterTags: 'Filter by tags',
    tagsPlaceholder: 'Tags (e.g. biosafety)',
    tagsLabel: 'Tags:',
    filterTaskStatus: 'Task Status',
    showDisabled: 'Show disabled',
    taskStatusPlaceholder: 'All task statuses',
    empty: 'No instruments found.',
    paginationPage: 'Page',
    paginationOf: 'of',
    paginationNoun: 'instruments',
    previous: 'Previous',
    next: 'Next',
    table: {
      label: 'Instrument',
      serial: 'Serial Number',
      department: 'Department',
      site: 'Site',
      status: 'Status',
      taskStatus: 'Task Status',
    },
    failures: 'failures',
    failuresResolved: 'failures (resolved)',
    serviceReports: 'Service Reports',
    taskStatus: {
      ODE: 'Overdue',
      DNW: 'Due Now',
      RDY: 'Ready',
      UTD: 'Up-to-date',
      // When the instrument type has no kept maintenance tasks (legacy N/A).
      empty: '-',
    },
    taskLogs: {
      titleNew: 'Log task',
      titleEdit: 'Edit task log',
      originalContextTitle: 'Original log context',
      originalCompletedBy: 'Completed by',
      originalLoggedAt: 'Logged at',
      originalComment: 'Comment',
      backToInstrument: 'Back to instrument',
      sectionTiming: 'Timing & notes',
      sectionNotes: 'Notes',
      completedAt: 'Completed at',
      loggedAt: 'Logged at',
      comments: 'Comments',
      commentsPlaceholder: 'Enter Comments...',
      commentRequiredHint: 'A comment is required because a measurement is unacceptable.',
      commentRequiredError: 'Please add a comment explaining the unacceptable measurement.',
      sectionMeasurements: 'Measurements',
      sectionDataFields: 'Data fields',
      unnamedField: 'Field',
      required: 'required',
      notRequired: 'not required',
      withinRange: 'Within acceptable range',
      textareaPlaceholder: 'Enter details',
      acceptableRange: 'Acceptable',
      validationRuleGt: 'Greater than',
      validationRuleGtEq: 'Greater than or equal to',
      validationRuleLt: 'Less than',
      validationRuleLtEq: 'Less than or equal to',
      validationRuleEquals: 'Equals',
      validationRuleBetween: 'Between',
      validationWarning: 'Value is outside acceptable range',
      number: {
        acceptable: 'Acceptable',
        unacceptable: 'Unacceptable',
      },
      linear: {
        acceptable: 'Acceptable',
        unacceptable: 'Unacceptable',
        acceptableHint: 'acceptable',
        valuePlaceholder: 'Enter measured value',
        unacceptableMismatch:
          'The entered value meets the acceptable condition. Choose "Acceptable" or enter an unacceptable value before submitting.',
      },
      fileAttachment: {
        chooseFile: 'Attach a file',
        replaceFile: 'Replace file',
        remove: 'Remove',
        sizeHint: 'PDF, Word, Excel, PNG, JPG and more. Max 20 MB.',
      },
      sectionLots: 'Supplies',
      lotsLinked: 'Supplies Linked',
      lotsUsed: 'Supplies Used',
      noExpiration: 'No Expiration',
      addLotRow: 'Add supply line',
      lot: 'Supply',
      lotDetail: 'Supply detail',
      selectLot: 'Select supply',
      selectLotDetail: 'Select supply',
      removeLotRow: 'Remove line',
      complianceWindow: 'Compliance window',
      phaseBeforeSuffix: 'before',
      phaseDueNow: 'Due Now',
      phaseOverdue: 'Overdue',
      phaseLeadTimeTooltip: 'Allowed {allowance} early',
      phaseDueNowTooltip: 'Due Now — task should be performed',
      phaseOverdueTooltip: 'Overdue after: {allowance}',
      lastPerformed: 'Last performed:',
      dayOffTitle: 'Today is a Day Off based on the {schedule} Schedule.',
      dayOffMaintenance:
        'However if testing is going to be performed, maintenance tasks need to be run now.',
      dayOffLastPerformed: 'Last Performed Date: {lastPerformedDate}',
      noPreviousLog: 'No previous log',
      intervalLabel: 'Interval:',
      hourSuffix: 'h',
      minuteSuffix: 'm',
      daySuffix: 'd',
      overdueBy: 'Overdue by',
      dueByPrefix: 'Due by',
      upToDatePlaceholder: 'up to date',
      completedAtNow: 'Current time',
      completedAtEdit: 'Edit',
      completedAtReset: 'Use current time',
      futureDateError: 'Date and time cannot be in the future',
      tooEarlyDateError: 'Date and time cannot be before 01/01/2010',
      completedAtRequired: 'A valid date and time is required.',
      modReasonLabel: 'Reason for date change',
      modReasonPlaceholder: 'Explain why the completion time was modified...',
      modReasonRequired: 'Reason is required when modifying completion time',
      sectionLogger: 'Performed by',
      completedByLabel: 'Completed by',
      completedByCurrentUser: 'Current user',
      linkedLotNoInventory:
        'The linked supply has no active inventory. Add a supply before logging this task.',
      logger: 'Logger',
      selectLogger: 'Select logger',
      noLoggers: 'No loggers configured.',
      addLogger: {
        trigger: 'Add new user',
        title: 'Add new user',
        firstNameLabel: 'First Name',
        lastNameLabel: 'Last Name',
        badgeIdLabel: 'Employee/Badge ID',
        badgeIdOptional: 'optional',
        save: 'Save',
        saving: 'Saving…',
        cancel: 'Cancel',
        error: 'Could not add the user. Please try again.',
      },
      submitting: 'Saving…',
      submit: 'Save',
      markComplete: 'Mark as Complete',
      noTesting: ncActionLabels.noTesting,
      noTestingSubmitting: 'Recording No Testing…',
      cancel: 'Cancel',
      checkRequiredFields: 'Please check required fields above.',
      fieldRequired: 'This field is required.',
      numericFieldInvalid: 'Enter a valid number.',
      validationOutOfRange: 'Value is out of the acceptable range.',
      lateConfirmTitle: 'Task will be marked as Late',
      lateConfirmMessage:
        'Based on the Completed Date, the task will be marked as "Late". Click "Yes" to confirm and save, or "Cancel" to re-enter the Completed Date.',
      dueNowConfirmMessage:
        'Based on the Completed Date, the task will be marked as "Late". Click "Yes" to confirm and save, or "Cancel" to re-enter the Completed Date.',
      beforeLastPerformedTitle: 'Date before last performed',
      beforeLastPerformedMessage:
        'The completed date is before the last performed date of {{lastPerformedDate}}. Are you sure you want to continue?',
      beforeLastPerformedConfirm: 'Yes, continue',
      lateConfirmConfirm: 'Yes, save as Late',
      lateConfirmCancel: 'Go back',
      lotInvalidWarningTitle: 'Supply issue — task blocked',
      lotMissingSelectionWarning: '{{lotName}}: No supply inventory selected',
      lotExpiredWarning: '{{lotName}}: Supply Expired',
      lotRetiredWarning: '{{lotName}}: Supply Retired',
      lotDiscardedWarning: '{{lotName}}: Supply Discarded',
      lotDataFieldsMissingWarning: 'Warning: DataField for the Supply selected is not specified.',
      lotNotSpecifiedWarning: 'Please specify {{lotName}} before Running this Task.',
      lotInvalidHint: 'Specify a new supply before running this task.',
      requiredLotMissingWarning:
        'The following supplies require a supply selection before completing this task:',
      voidLog: 'Void log',
      voidDialogTitle: 'Void this task log?',
      voidDialogDescription:
        'The log will be marked inactive and kept for audit. A reason is required for the compliance record.',
      voidReasonLabel: 'Reason (required)',
      voidReasonPlaceholder: 'e.g. duplicate entry, entered in error',
      voidReasonRequired: 'A reason is required to void this task log.',
      voidCancel: 'Cancel',
      voidConfirm: 'Void log',
      voidSubmitting: 'Voiding…',
      errors: {
        required_field_invalid: 'This field is required.',
      },
      attachments: {
        sectionTitle: 'Attachments',
        empty: 'No attachments yet.',
        uploadLabel: 'Upload attachment',
        chooseFile: 'Choose a file',
        orDragDrop: 'or drag and drop',
        allowedTypes: 'PDF, Word, Excel, PNG, JPG and more',
        descriptionLabel: 'Description (optional)',
        descriptionPlaceholder: 'Brief description of the file',
        uploading: 'Uploading…',
        upload: 'Upload',
        downloadSr: 'Download',
        deleteSr: 'Delete',
      },
      commentsPanel: {
        sectionTitle: 'Comments',
        empty: 'No comments yet.',
        addLabel: 'Add a comment',
        placeholder: 'Write your comment…',
        saving: 'Saving…',
        save: 'Save',
        cancel: 'Cancel',
        edit: 'Edit',
        deleting: 'Deleting…',
        delete: 'Delete',
        moreActions: 'Comment actions',
        deleteConfirmTitle: 'Delete comment?',
        deleteConfirmDescription:
          'This comment will be permanently removed. This action cannot be undone.',
        posting: 'Posting…',
        post: 'Post comment',
        statusCurrent: 'Current',
        statusResolved: 'Resolved',
        resolve: 'Resolve',
        resolving: 'Resolving…',
      },
    },
    taskDataFields: {
      indexTitle: 'Task data fields',
      indexSubtitle:
        'Dynamic fields, validation rules, and picklist values configured for this task.',
      formTitleNew: 'New data field',
      formTitleEdit: 'Edit data field',
      backToList: 'Back to list',
      addField: 'Add data field',
      editInAdmin: 'Edit in Admin',
      typeLevelNotice:
        'Data fields are defined at the instrument-type level and apply to all instruments of this type. To change them, edit the instrument type in Admin.',
      tableLabel: 'Label',
      tableType: 'Type',
      tableRequired: 'Required',
      tableActive: 'Active',
      edit: 'Edit',
      empty:
        'No data fields yet. Data fields for this task are configured in Admin → Instrument Types.',
      fieldLabel: 'Label',
      fieldLabelPlaceholder: 'e.g. Temperature reading',
      fieldDescription: 'Description (optional)',
      fieldDescriptionPlaceholder: 'e.g. Record the result in °C',
      fieldType: 'Data type',
      fieldSelectType: 'Select a type',
      fieldTypeRequired: 'Data type is required.',
      fieldRequired: 'Required at log time',
      fieldRequiresValidation: 'Enforce validation rules at log time',
      fieldIsConfidential: 'Is Confidential',
      fieldActive: 'Field is active (shown on log form)',
      sectionAcceptable: 'Acceptable values & rules',
      acceptableEmptyHint:
        'Optional. Without rules, any entered value is treated as valid when the field does not use strict validation at log time.',
      acceptableAddRow: 'Add More Validation',
      acceptableValue: 'Value / pattern',
      acceptableValuePlaceholder: 'e.g. Pass',
      acceptableBetweenMin: 'Min',
      acceptableBetweenMax: 'Max',
      acceptableBetweenInvalid: "Please enter valid 'between' values (min must not exceed max).",
      acceptableValidationType: 'Validation type',
      acceptableIfHeader: 'Acceptable if',
      acceptableIsAcceptable: 'Treated as passing',
      acceptableRowActive: 'Rule active',
      sectionDefined: 'Selection values (pick list)',
      definedAddRow: 'Add More Validation',
      definedValue: 'Display value',
      definedIsAcceptable: 'Acceptable',
      definedRemove: 'Remove',
      submit: 'Save',
      submitting: 'Saving…',
      yes: 'Yes',
      no: 'No',
      cancel: 'Cancel',
    },
    show: {
      backToInstruments: '← Back to instruments',
      active: 'Active',
      inactive: 'Inactive',
      editButton: 'Edit',
      commentsButton: 'Comments',
      cloneButton: 'Clone',
      transferButton: 'Move Instrument',
      updateStatusButton: 'Update Status',
      updateStatusLockedHint:
        'Status cannot be changed while the instrument has an Unresolved Issue. It clears automatically when a passing QC result is logged.',
      noData: '—',
      tabs: {
        info: 'Info',
        tasks: 'Tasks',
        lots: 'Supplies',
        compliance: 'Compliance',
        reports: 'Reports',
        history: 'History',
      },
      documents: {
        toggle: 'Documents ({count})',
        downloadSr: 'Download',
      },
      serviceReports: {
        addButton: 'Add Service Report',
        dialogTitle: 'Add Service Report',
        completedAtLabel: 'Time completed',
        descriptionLabel: 'Description',
        descriptionPlaceholder: 'Describe the service performed…',
        descriptionRequired: 'Description is required.',
        loggerLabel: 'Logged by',
        loggerPlaceholder: 'Select a logger (optional)',
        attachmentsLabel: 'Attachments',
        chooseFiles: 'Choose files',
        allowedTypes: 'PDF, Word, Excel, PNG, JPG and more',
        invalidFileType:
          'Invalid file type. Allowed formats: jpg, jpeg, png, gif, tiff, pdf, doc, docx, rtf, txt, csv, xls, xlsx, xlsm, pptx, zip.',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        saved: 'Service report added.',
        saveFailed: 'Failed to save the service report. Please try again.',
        completedByLabel: 'Completed by',
      },
      info: {
        type: 'Type',
        location: 'Location',
        manufacturer: 'Manufacturer',
        model: 'Model',
        description: 'Description',
        serialNumber: 'Serial Number',
        barcode: 'Barcode',
        department: 'Department',
        unit: 'Unit',
        site: 'Site',
        status: 'Status',
        dateOfPurchase: 'Date of Purchase',
        cost: 'Cost',
        compliancePercent: 'Compliance %',
        tags: 'Tags',
      },
      tasks: {
        name: 'Task',
        intervalType: 'Interval Type',
        status: 'Status',
        actions: 'Actions',
        logTask: 'Log',
        logTaskTitle: 'Record or continue this task log',
        taskHistory: 'Task history',
        taskHistoryTitle: 'View task log history and exports',
        configureFields: 'Data fields',
        configureFieldsTitle: 'Configure dynamic fields and validation for this task',
        empty: 'No tasks assigned.',
        loading: 'Loading tasks…',
        noScheduledTasks: 'No scheduled tasks.',
        linkedTo: 'Linked to',
        tagsLabel: 'Tags:',
        notesLabel: 'Notes',
        sortBy: {
          label: 'Sort By',
          status: 'Status',
          default: 'Default',
          frequency: 'Frequency',
          alphabetical: 'A-Z',
          tag: 'Tag',
          sections: {
            overdue: 'Overdue',
            dueNow: 'Due Now',
            ready: 'Ready',
            upToDate: 'Up-to-Date',
            unscheduled: 'Unscheduled',
            scheduledTasks: 'Scheduled Tasks',
            unscheduledTasks: 'Unscheduled Tasks',
          },
        },
        specifyLotBanner: 'Please specify {lotName} before Running this Task.',
        selectLotAction: 'Select Supply',
        cannotTestAlert: '{status}! No tasks can be completed at this time.',
        cannotTestAlertNoStatus: 'No tasks can be completed at this time.',
        failedNeedsRerun: {
          label: 'Failed',
          hint: '(needs to be re-run)',
        },
        viewLastPerformed: 'View Last Performed',
        viewChart: 'View Chart',
        chartDialogTitle: 'Data Field Chart',
        chartNoData: 'No data points recorded yet.',
        chartLoading: 'Loading chart data…',
        chartError: 'Failed to load chart data.',
        chartClose: 'Close',
        chartAcceptable: 'Acceptable',
        chartUnacceptable: 'Unacceptable',
        chartAcceptableRange: 'Acceptable range',
        chartFrom: 'From',
        chartTo: 'To',
        chartApply: 'Apply',
        changeLog: {
          title: 'Change Log',
          empty: 'No version history available for this task.',
          instrumentType: 'Instrument Type',
          created: 'Created',
          updatedBy: 'Updated By',
          taskName: 'Maintenance Task Name',
          frequencyType: 'Frequency Type',
          frequency: 'Frequency',
          startAt: 'Start At',
          endsAtMidnight: 'Ends At 23:59:59',
          affectsCompliance: 'Affects Compliance',
          dataFields: 'Data Fields',
          field: 'Field',
          fieldType: 'Field Type',
          validateField: 'Validate Field',
          enteredValue: 'Entered Value',
          yes: 'YES',
          no: 'NO',
          none: 'None',
        },
      },
      lots: {
        empty: 'No supplies found.',
        toggleHideExpired: 'Hide expired',
        toggleHideRetired: 'Hide retired',
        visibilityFilterExplanation:
          'Expired and retired supplies are hidden by default. Filters do not retire or delete inventory.',
      },
      compliance: {
        rollingPercent: 'Rolling Compliance %',
        failureCount: 'Failure Count',
        serviceReportCount: 'Service Reports',
        openNonComplianceCount: 'Open Non-Compliances',
        viewDashboard: 'View Compliance Dashboard →',
      },
      failureWarning: {
        title: 'WARNING',
        message: 'Failed {count} time(s) in the past {days} days',
        resolvedTitle: 'RESOLVED',
        resolvedMessage:
          'Failed {count} time(s) in the past {days} days. Every failure has since been corrected.',
        viewDetails: 'View Details',
      },
      currentComment: {
        title: 'Current comment',
        message: 'This instrument has a current comment.',
        messageCount: 'This instrument has {count} current comments.',
        view: 'View comment',
      },
      reports: {
        filters: {
          from: 'From',
          to: 'To',
          tasks: 'Tasks',
          apply: 'Apply',
        },
        showTaskFrequencyDetails: 'Show task frequency details',
        pager: {
          previous: 'Previous month',
          next: 'Next month',
        },
        grid: {
          taskColumn: 'Task',
          failureRow: 'Failure',
        },
        // Default empty grid (no task rows for the month). Filter-specific copy is emptyFilter.
        empty: 'No task entries for this period.',
        emptyFilter: 'No tasks match the current filter.',
      },
      history: {
        departmentTransfers: 'Department Transfers',
        statusLog: 'Status History',
        taskHistory: 'Task History (Change Log)',
        noTransfers: 'No department transfers recorded.',
        noStatusChanges: 'No status changes recorded.',
        noTaskEvents: 'No task events recorded.',
        showAlertsOnly: 'Showing Alert only',
        showFullDetails: 'Showing Full Details',
        successfulEventHidden: '({count} Successful Event Hidden)',
        successfulEventsHidden: '({count} Successful Events Hidden)',
        noAlertEvents: 'No alert events to show.',
        from: 'From',
        to: 'To',
        changedBy: 'Changed By',
        changedAt: 'Date',
        completedAt: 'Completed At',
        comment: 'Comment',
        limitReached: 'Showing the 50 most recent records.',
        taskFailed: 'Failed',
        performedBy: 'Performed By',
        performedAt: 'Performed',
        dateModifiedReason: 'Date Modified Reason',
        taskEventColumn: 'Event',
        taskNameColumn: 'Task',
        taskExecutionHistory: 'Task Execution History',
      },
    },
    statusUpdate: {
      pageTitle: 'Update Instrument Status',
      statusField: 'New Status',
      commentField: 'Comment (optional)',
      completedAtField: 'Completed At',
      submit: 'Update Status',
      cancel: 'Cancel',
      success: 'Instrument status updated successfully.',
      unchanged: 'Instrument is already in this status.',
    },
    transfer: {
      pageTitle: 'Move Instrument',
      currentLocationTitle: 'Current location',
      siteField: 'New Site',
      departmentField: 'New Department',
      unitField: 'New Unit',
      commentField: 'Comment (optional)',
      submit: 'Move',
      cancel: 'Cancel',
      success: 'Instrument transferred successfully.',
      unchanged: 'Instrument is already assigned to this department and unit.',
      blocked: {
        title: 'Transfer not allowed',
        triggered_tasks:
          'This instrument has triggered tasks (Ready, Due Now, or Overdue). Complete or resolve them before transferring the instrument.',
        open_non_compliances:
          'This instrument has open non-compliances. Review or resolve them before transferring the instrument.',
        acknowledge: 'OK',
      },
    },
    lotsCompare: {
      title: 'Compare Supply',
      backToLot: '← Back to Supply',
      compareLots: 'Compare Supplies',
      compareAgainst: 'Compare against',
      selectComparison: 'Select a supply to compare against',
      selectPlaceholder: 'Select a supply…',
      sideByTitle: 'Side-by-Side Comparison',
      field: 'Field',
      primary: 'Primary Supply',
      selected: 'Comparison Supply',
      historyTitle: 'Comparison History',
      historyDate: 'Date',
      validated: 'Result',
      yes: 'Validated',
      no: 'Rejected',
      loading: 'Loading comparison data…',
      noDifferences: 'All fields are identical',
      differenceCount: '{count} difference(s) found',
      showDifferencesOnly: 'Differences only',
      allFieldsMatch: 'All fields match — supplies are identical',
      emptyState: 'Select a supply from the dropdown above to see a side-by-side comparison',
      fields: {
        lotNumber: 'Lot Number',
        manufacturer: 'Manufacturer',
        product: 'Product',
        status: 'Status',
        expiration: 'Expiration',
        openedExpiration: 'Opened Expiration',
        dateReceived: 'Date Received',
        dateOpened: 'Date Opened',
        site: 'Site',
        isConfirmed: 'Confirmed',
        comments: 'Comments',
      },
    },
    lotsForm: {
      createTitle: 'New Supply',
      editTitle: 'Edit Supply',
      addDetailTitle: 'Add Supply',
      dataFieldValuesTitle: 'Data Field Acceptable Values',
      dataFieldValuesWillNeedUpdated: 'Select data field acceptable values',
      linkedToTask: 'Linked to task',
      noDataFieldValuesConfigured: 'No acceptable values configured for this supply yet.',
      acceptableIf: 'Acceptable if',
      acceptableBetweenMin: 'Min',
      acceptableLabel: 'Acceptable',
      notAcceptableLabel: 'Not acceptable',
      addMoreValidation: '+ Add More Validation',
      betweenValueSeparator: 'to',
      operatorOr: 'OR',
      operatorAnd: 'AND',
      dialog: {
        loading: 'Loading form…',
        loadError: 'Failed to load the form. Please try again.',
      },
      backToInstrument: '← Back to instrument',
      created: 'Supply created successfully.',
      updated: 'Supply updated successfully.',
      secondaryLotAdded: 'Secondary supply added.',
      secondaryLotRemoved: 'Secondary supply removed.',
      placeholders: {
        optional: 'Optional',
        optionalDate: '(optional)',
        lotName: 'Enter Supply Name',
        enterLotDetailNumber: 'Enter supplies number',
        secondaryLotName: 'Secondary component name',
      },
      hints: {
        controlReagentNameExample: 'Example: Supply 1',
        isKitIntro:
          'Check the "Is a Kit" box if the supply contains multiple components with lot #s and expiration dates.',
        isKitAddSecondary:
          'Notice the "Add Secondary" Box appears at the bottom of the screen when this box is checked.',
        requiresValidation:
          'Check the "Requires Validation" box if the supply requires a validation process before being placed "In Use"',
      },
      errors: {
        lotNumberRequired: 'Supplies Number is required',
        openedExpirationExceedsExpiration:
          'Opened expiration date must not be later than expiration date.',
        expirationInPast: 'Expiration date should not be lesser than current date',
        expirationBeforeReceived: 'Expiration date cannot be earlier than date received',
        dateReceivedInFuture: 'Date received cannot be in the future',
        openBeforeReceived: 'Open date cannot precede received date',
        inUseBeforeReceived: 'In use date cannot precede received date',
        discardedBeforeReceived: 'Discarded date cannot precede received date',
        dateInUseInFuture: 'In use date cannot be in the future',
        dateDiscardedInFuture: 'Discarded date cannot be in the future',
        saveFailed: 'Could not save. Please fix the highlighted fields below.',
        definedValueAcceptableRequired: 'Select data field acceptable values',
        dataFieldValueRequired: 'Select data field acceptable values',
        betweenValuesInvalid: 'Please fill valid between values.',
      },
      sections: {
        lotInfo: 'Supply Information',
        lotDetail: 'Supply Inventory',
        secondaryLots: 'Secondary Supplies',
      },
      fields: {
        name: 'Add a Supply Name',
        notes: 'Notes',
        isKit: 'Is a Kit',
        requiresValidation: 'Requires Validation',
        lotNumber: 'Supplies Number',
        manufacturer: 'Manufacturer',
        product: 'Product #',
        description: 'Description',
        site: 'Site',
        dateReceived: 'Date Received',
        dateOpened: 'Date Opened',
        dateInUse: 'Date In Use',
        expirationDate: 'Expiration Date',
        openedExpiration: 'Opened Expiration',
        dateDiscarded: 'Date Discarded',
        otherIdentifier: 'Other Identifier',
        comments: 'Comments',
        useAllSecondary: 'Use All Secondary',
        isConfirmed: 'Is Confirmed',
        validationStatus: 'Validation Status',
        validated: 'Validated',
        notValidated: 'Not Validated',
      },
      validationConfirm: {
        title: 'Confirmation',
        message:
          'This lot requires validation before it can be used. Please ensure the lot has been validated before entering the “Date in Use“',
        confirm: 'Yes',
        cancel: 'No',
      },
      buttons: {
        save: 'Save Supply',
        cancel: 'Cancel',
        deleteSecondaryDraft: 'Delete Secondary',
        addSecondary: '+ Add Secondary',
        addSecondaryLot: 'Add secondary supply',
        saveSecondaryLot: 'Save secondary supply',
        removeSecondaryLot: 'Remove',
      },
      noSecondaryLots: 'No secondary supplies recorded.',
      secondaryDraftRowLabel: 'Secondary component',
      secondary: {
        catalogSourceNew: 'New',
        catalogSourceExisting: 'Existing',
        pickExistingSecondaryNamePlaceholder: 'Select secondary name…',
        existingSecondaryLotNumberTypeOrSuggestPlaceholder:
          'Type lot number — or open saved numbers with the chevron button',
        savedLotNumbersDropdownChevronAriaLabel: 'Show saved lot numbers list',
        lotNumberHintFilterMissEmptyStateCaption: 'Nothing matches what you typed — keep typing',
        selectNameBeforeLotNumberPlaceholder: 'Select secondary name first…',
        emptySecondaryCatalogPlaceholder: 'No saved components yet — add one with New first',
      },
      attachments: {
        sectionTitle: 'Add new Files',
        empty: 'No files attached to this supply yet.',
        fileUpload: 'File upload',
        fileNameReadonly: 'File name',
        fileNamePlaceholder: 'No file selected',
        chooseFile: 'Choose file',
        orDragDrop: 'or drag and drop',
        allowedTypes: 'PDF, Word, Excel, PNG, JPG and more',
        descriptionLabel: 'File description (optional)',
        descriptionPlaceholder: 'Optional',
        uploading: 'Uploading…',
        upload: 'Upload',
        downloadSr: 'Download',
        openFileSr: 'Open',
        fallbackFilename: 'download',
        downloading: 'Downloading…',
        downloadFailed: 'Error while downloading. Please contact system admin.',
        downloadSessionExpired:
          'Your session expired. Please sign in again and retry the download.',
        deleteSr: 'Delete',
        clearFile: 'Clear file',
        addFile: 'Add new file',
      },
    },
    lotsShow: {
      title: 'Supply Details',
      back: 'Back',
      yes: 'Yes',
      no: 'No',
      active: 'Active',
      inactive: 'Inactive',
      sections: {
        lotInfo: 'Supply Information',
        secondaryLots: 'Secondary Supplies',
        usageHistory: 'Usage History',
      },
      noSecondaryLots: 'No secondary supplies recorded.',
      noUsageHistory: 'No usage history recorded.',
      editLot: 'Edit Supply',
      table: {
        task: 'Task',
        usedAt: 'Used At',
        active: 'Active',
        name: 'Name',
        lotNumber: 'Lot Number',
        manufacturer: 'Manufacturer',
        product: 'Product',
        description: 'Description',
        site: 'Site',
        received: 'Received',
        opened: 'Opened',
        inUse: 'In Use',
        expiration: 'Expiration',
        openedExpiration: 'Opened Expiration',
        otherIdentifier: 'Other ID',
        comments: 'Comments',
        isConfirmed: 'Is Confirmed',
        requiresValidation: 'Requires Validation',
        status: 'Status',
        retirementComment: 'Retirement comment',
      },
    },
    lotsRetire: {
      dialogTitle: 'Confirmation',
      dialogLead: 'Are you sure you want to Retire this Supply?',
      dialogNote: 'NOTE: This action cannot be undone.',
      commentsLabel: 'Comments',
      commentsPlaceholder: 'Comments',
      cancelButton: 'Cancel',
      confirmButton: 'Retire Supply',
    },
    controlsReagents: {
      manageButton: 'Manage Supplies',
      dialogTitle: 'Choose Supply',
      addLotDetailDialogTitle: 'Add Supply',
      cloneLotDialogTitle: 'Clone Supply',
      missingLot: 'This task needs a supply on the instrument type before you can assign a detail.',
      hideExpiredLots: 'Hide expired',
      hideRetiredLots: 'Hide retired',
      availableLotsHeading: 'Available supplies',
      actionsHeading: 'Actions',
      emptyFilteredLots: 'No matching supplies.',
      selectButton: 'Select',
      selectedButton: 'Selected',
      cloneLotButton: 'Clone',
      editLotButton: 'Edit Supply',
      addLotDetailButton: '+ Add Supply',
      retireLotButton: 'Retire',
      backToLotsButton: 'Back to supplies',
      closeButton: 'Close',
    },
    lotsIndex: {
      title: 'Inventory Supplies',
      backToInstrument: '← Back to',
      addLotDetail: 'Add Supply',
      noLots: 'No supplies found for this instrument type.',
      noLotDetails: 'No supplies found.',
      kit: 'Kit',
      requiresValidation: 'Requires Validation',
      expired: 'Expired',
      retire: 'Retire',
      table: {
        lotNumber: 'Supplies Number',
        status: 'Status',
        expiration: 'Expiration',
        site: 'Site',
        received: 'Received',
        opened: 'Opened',
      },
      lifecycleStatus: {
        retired: 'Retired',
        discarded: 'Discarded',
        expired: 'Expired',
        in_use: 'In Use',
        opened: 'Opened',
        received: 'Received',
        pending: 'Pending',
      },
      // Legacy lotsExpireAlert copy (JW 407 / REBUILD-402) — do not reuse reports.logDetails.lotsExpireAlert.
      lotsExpireAlert: {
        week: 'Lots about to expire in a week',
        today: 'Lots about to expire Today',
        day: 'Lots about to expire in a day',
        days: 'Lots about to expire in {count} days',
      },
    },
    reports: {
      cell: {
        successAria: 'View log details for {task} on {date}',
        nptAria: 'View No Testing entry for {task} on {date}',
        missedAria: 'Review missed date for {task} on {date}',
        successTooltip: 'Completed',
        failureTooltip: 'Missed',
        overrideTooltip: ncActionLabels.override,
        confirmedTooltip: 'Confirmed',
        noTestingTooltip: ncActionLabels.noTesting,
        dayOff: 'Off',
        dateModifiedSuffix: ' (date-modified)',
        dateModifiedLabel: '(date-modified)',
        inactiveTaskSuffix: '(Inactive)',
      },
      // Canonical calendar cell-symbol labels — the single source consumed by
      // CELL_SYMBOL_MAP (calendar-cell-symbol.ts) for tooltips + the derived legend.
      cellSymbols: {
        success: 'Complete',
        successDateChanged: completeDateChangedLabel,
        failure: ncActionLabels.unreviewed,
        override: ncActionLabels.override,
        confirmed: ncActionLabels.confirmed,
        noTesting: ncActionLabels.noTesting,
        dayOff: 'Day Off',
      },
      resolvedDetail: {
        reviewedBy: 'Reviewed by',
        resolutionType: 'Resolution',
        performedBy: 'Performed by',
        loggedAt: 'Entered',
        performedAt: 'Performed',
      },
      missedDate: {
        title: 'Log Details',
        status: 'Missed Date',
        overdueBadge: 'Overdue',
        back: 'Back',
      },
      listView: {
        colTask: 'Task',
        colUser: 'Completing User',
        colDate: 'Date',
        // Period/filter empty copy lives on show.reports (CalendarGrid only). ListView
        // returns null when the grid already shows taskRows; alert-only uses emptyAlerts.
        emptyAlerts: 'No alert entries for this period.',
        // Reason line on a result row (REBUILD-607). Legacy wording: a date modification reads
        // "Date Modified Reason:"; an NC confirmation and an override both read "Explanation:".
        reasonLabel: {
          dateModification: 'Date Modified Reason',
          nonCompliant: 'Explanation',
          override: 'Explanation',
        },
      },
      legend: {
        failure: ncActionLabels.unreviewed,
        dateModification: 'Date Modification',
        serviceReport: 'Service Report',
        statusChanges: 'Status Changes',
        departmentMoved: 'Department Moved',
        unscheduled: 'Unscheduled',
        symbolTitle: 'Symbols',
      },
      extraRows: {
        serviceReports: 'Service Reports',
        dateModification: 'Date Modification',
        statusChanges: 'Status Changes',
        departmentMoved: 'Department Moved',
        unscheduled: 'Unscheduled',
      },
      serviceReports: {
        dialogTitle: 'Service Report',
        sectionTitle: 'Service Reports',
        completedBy: 'Completed by:',
        completedAt: 'Time completed:',
        descriptionLabel: 'Description',
        attachmentsLabel: 'Attachment(s)',
        comments: 'Comments',
        addComment: 'Add comment',
        commentPlaceholder: 'Write a comment…',
        submitComment: 'Post',
        deleteComment: 'Delete',
        noComments: 'No comments yet.',
        empty: 'No service reports recorded.',
        table: {
          description: 'Description',
          completedBy: 'Completed by',
          date: 'Date',
        },
      },
      statusChanges: {
        dialogTitle: 'Status Change(s)',
      },
      departmentMoves: {
        dialogTitle: 'Department Move(s)',
      },
      dialogs: {
        empty: 'No records for this date.',
      },
      logDetails: {
        title: 'Log Details',
        complete: 'Complete',
        failure: 'Failure',
        npt: ncActionLabels.noTesting,
        completedBy: 'Completed by',
        completedByOn: 'on',
        completedAt: 'Date & time completed',
        backdatedLabel: 'Back Dated',
        backdatedPerformedAt: 'Performed:',
        scheduleFrequency: 'Schedule Frequency',
        calendarScheduleFrequency: 'Calendar Schedule Frequency',
        leadTime: 'Lead time {hours} hours',
        overdueAfter: 'Overdue after {hours} hours',
        comments: 'Comments',
        lotsUsed: 'Supplies Used',
        currentLots: 'Supplies',
        completeDateModified: 'Complete (Date Modified)',
        nptDateModified: 'No Testing (Date Modified)',
        dateModified: 'Date Modified',
        dateModifiedReason: 'Date Modified Reason:',
        // Legacy label for the NC-confirmation / override comment (calendarView.template.html).
        explanationLabel: 'Explanation',
        dateChangedTitle: 'Date Change',
        dateChangedOriginal: 'Original date',
        dateChangedNew: 'Changed to',
        lotsExpireAlert: 'Supplies about to expire',
        lotDetail: {
          lotNumber: 'Lot Number',
          expirationDate: 'Expiration Date',
          openedExpiration: 'Opened Expiration',
          otherIdentifier: 'Other Identifier',
          manufacturer: 'Manufacturer',
          description: 'Description',
          dateReceived: 'Date Received',
          dateOpened: 'Date Opened',
          dateInUse: 'Date In Use',
          dateDiscarded: 'Date Discarded',
          product: 'Product #',
          noExpiration: 'No Expiration',
        },
        dataFields: 'Data Fields',
        lotNumber: 'Lot #',
        manufacturer: 'Manufacturer',
        product: 'Product',
        expires: 'Expires',
        attachments: 'Attachments',
        download: 'Download',
        showDetails: 'Show Details',
        hideDetails: 'Hide Details',
        addComment: 'Add Comment',
        cancel: 'Cancel',
        acceptableRange: 'Acceptable',
        close: 'Close',
        notFound: 'This log entry is no longer available.',
        error: 'Failed to load log details.',
        prevDay: 'Prev day',
        nextDay: 'Next day',
        prevDayAria: 'Previous day',
        nextDayAria: 'Next day',
        dateStripAria: 'Jump to day {day}',
        missedDate: 'Missed Date',
        customTask: 'Custom Task',
        voidLog: 'Void log',
        voidDialogTitle: 'Void this task log?',
        voidDialogDescription:
          'This action cannot be undone. The log will be marked as voided and an audit record will be created.',
        voidReasonLabel: 'Reason (required)',
        voidReasonPlaceholder: 'e.g. entered in error, duplicate entry',
        voidReasonRequired: 'A reason is required to void this task log.',
        voidTaskIdMissing:
          'Unable to void this log because its task could not be determined. Close and try again.',
        voidCancel: 'Cancel',
        voidConfirm: 'Void log',
        voidSubmitting: 'Voiding…',
        noExpiration: 'No Expiration',
      },
      dayOccurrences: {
        dialogTitle: 'Occurrences for {date}',
        empty: 'No occurrences for this date.',
        loading: 'Loading occurrences…',
        error: 'Failed to load occurrences.',
        statusFailure: 'Missed',
        statusExecutedFailure: 'Failure',
        statusNoTesting: ncActionLabels.noTesting,
        statusOverride: ncActionLabels.override,
        statusConfirmed: ncActionLabels.confirmed,
        completedNoDetail: 'Completed — no log detail available.',
        explanationLabel: 'Explanation',
        reviewedBy: 'Reviewed by {actor} on {date}',
        confirmedAt: 'Confirmed',
      },
      failures: {
        dialogTitle: 'Failure(s)',
        aria: 'View failures for {date} ({count})',
        empty: 'No failures for this date.',
      },
      signOffHistory: {
        title: 'Sign-off History',
        empty: 'No Signatures at this time.',
        undone: '(undone)',
      },
    },
    form: {
      newTitle: 'New Instrument',
      editTitle: 'Edit Instrument',
      backToInstruments: '← Back to instruments',
      labelField: 'Instrument Label',
      labelFieldPlaceholder: 'e.g. Centrifuge A1',
      serialNumberField: 'Serial Number',
      serialNumberPlaceholder: 'e.g. SN-123456',
      barcodeField: 'Barcode',
      barcodePlaceholder: 'Scan or enter barcode',
      costField: 'Cost',
      costPlaceholder: 'e.g. 1500.00',
      dateOfPurchaseField: 'Date of Purchase',
      typeField: 'Instrument Type',
      typeLockedHint: 'Preselected from the instrument type page.',
      siteField: 'Site',
      departmentField: 'Department',
      unitField: 'Unit',
      tagsField: 'Tags',
      logAuthentication: 'Log Authentication',
      logAuthenticationOpen: 'Open logging, no password',
      logAuthenticationAuthenticated: 'Authenticated users only',
      followParentScheduleLabel: 'Follow Parent Schedule',
      noDepartment: 'No department',
      noUnit: 'No unit',
      selectType: 'Select an instrument type',
      selectSite: 'Select a site',
      selectDepartment: 'Select a department',
      selectUnit: 'Select a unit',
      labelRequired: 'Instrument Label is required.',
      typeRequired: 'Instrument Type is required.',
      siteRequired: 'Site is required.',
      departmentRequired: 'Department is required.',
      unitRequired: 'Unit is required.',
      submitCreate: 'Create Instrument',
      submitUpdate: 'Save Changes',
      cancelButton: 'Cancel',
      addInstrumentDialogTitle: 'Add Instrument — {type}',
      created: 'Instrument created successfully.',
      updated: 'Instrument updated successfully.',
      addTagPlaceholder: 'Add tags…',
      searchTagsPlaceholder: 'Search tags…',
      noTagsFound: 'No tags found.',
      createTag: 'Create "{name}"',
      creatingTag: 'Creating…',
      createTagFailed: 'Could not create tag. Please try again.',
    },
    tasksSchedule: {
      instrumentTaskPickerTruncated:
        'Showing {shown} of {total} instrument tasks (list is capped for performance). If a task is missing, contact an administrator.',
      yes: 'Yes',
      no: 'No',
      form: {
        timeUnits: {
          minutes: 'Minutes',
          hours: 'Hours',
          days: 'Days',
          weeks: 'Weeks',
          months: 'Months',
          years: 'Years',
        },
        rrule: {
          sectionTitle: 'Recurrence rule',
          builderFrequency: 'Repeats',
          builderInterval: 'Every',
          builderIntervalUnit: 'interval',
          builderDays: 'On days',
          builderPreview: 'Pattern preview',
          freqMinutely: 'Minutely',
          freqHourly: 'Hourly',
          freqDaily: 'Daily',
          freqWeekly: 'Weekly',
          freqMonthly: 'Monthly',
          freqYearly: 'Yearly',
          dayMon: 'Mon',
          dayTue: 'Tue',
          dayWed: 'Wed',
          dayThu: 'Thu',
          dayFri: 'Fri',
          daySat: 'Sat',
          daySun: 'Sun',
          weeklyModeNone: 'None (every N weeks)',
          weeklyModeSelectDays: 'Select specific days',
          monthlyModeNone: 'None — anchor from first execution',
          monthlyModeSpecificDay: 'Specific day of month',
          monthlyModeRelative: 'Relative (e.g. first Monday)',
          monthlyDayLabel: 'Day of month',
          monthlyDayShortMonthHint: 'Months with fewer days will use their last day instead.',
          monthlyPositionLabel: 'Position',
          monthlyWeekdayLabel: 'Day of week',
          monthlyPositionFirst: 'First',
          monthlyPositionSecond: 'Second',
          monthlyPositionThird: 'Third',
          monthlyPositionFourth: 'Fourth',
          monthlyPositionLast: 'Last',
          yearlyModeNone: 'None (every N years)',
          yearlyModeSpecificDay: 'Specific day and month',
          yearlyDayLabel: 'Day of month',
          yearlyMonthLabel: 'Month',
          nextTriggerLabel: 'Next trigger',
          overdueDateLabel: 'Overdue after',
          endsAtMidnight: 'Task window ends at midnight (23:59:59 of the due day)',
          monthlyModeLabel: 'Monthly pattern',
          yearlyModeLabel: 'Yearly pattern',
          yearlyMonthJanuary: 'January',
          yearlyMonthFebruary: 'February',
          yearlyMonthMarch: 'March',
          yearlyMonthApril: 'April',
          yearlyMonthMay: 'May',
          yearlyMonthJune: 'June',
          yearlyMonthJuly: 'July',
          yearlyMonthAugust: 'August',
          yearlyMonthSeptember: 'September',
          yearlyMonthOctober: 'October',
          yearlyMonthNovember: 'November',
          yearlyMonthDecember: 'December',
        },
      },
      taskForm: {
        intervalType: 'Frequency definition',
        pattern: 'Pattern',
        overdue: 'Overdue',
        affectsCompliance: 'Affects compliance',
        effectiveAt: 'Effective at',
        endAt: 'End at',
        submit: 'Save task scheduling',
        frequencyAssignmentHeading: 'Frequency assignment',
        saveFrequencyAssignment: 'Save frequency assignment',
      },
    },
    taskFrequencyOverview: {
      title: 'Task frequency overview',
      intro:
        'Read-only list of frequency definitions and assigned tasks, with an upcoming-due preview when the schedule supports it.',
      backToInstruments: '← Back to instruments',
      definitionsHeading: 'Frequency definitions',
      tasksHeading: 'Instrument tasks and timeline preview',
      colTask: 'Task',
      colInstrument: 'Instrument',
      colFrequencyDefinition: 'Frequency definition',
      colFrequencyType: 'Frequency type code',
      colLeadLate: 'Lead / late (hours)',
      colTimeline: 'Upcoming preview',
      leadLateHeading: 'Lead / late (hours)',
      noInstrument: '—',
      timelineStripTitle:
        'Markers show upcoming due times within a 90-day preview window from today.',
      timelineLane: {
        computed: 'Upcoming due times calculated from the last completion and interval.',
        notComputed: {
          generic: 'Timeline not computed for this row.',
          missing_payload: 'Timeline data was not available.',
          missing_interval_type: 'No frequency definition on this task.',
          calendar_fixed: 'Calendar-fixed tasks do not show an interval-based preview here.',
          unscheduled: 'Unscheduled tasks do not show an interval-based preview here.',
          not_timed: 'Only timed frequency tasks show an interval-based preview here.',
          missing_interval_hours: 'This timed definition is missing a positive interval in hours.',
          no_completion_log: 'No active completion log found; next due times cannot be derived.',
          no_upcoming_within_window: 'No upcoming due times fall within the effective window.',
        },
      },
    },
    taskLogHistory: {
      addLog: 'Add log',
      title: 'Task log history',
      inactiveSuffix: '(Inactive)',
      filterStatus: 'Log status',
      statusAll: 'All',
      statusActive: 'Active only',
      statusVoided: 'Voided only',
      filterLoggedFrom: 'Logged from',
      filterLoggedTo: 'Logged to',
      filterDateHelp:
        'Date filters use the log’s logged date (start of “from” day through end of “to” day).',
      colLoggedAt: 'Logged at',
      colCompletedAt: 'Completed at',
      submittedAt: 'Submitted',
      colStatus: 'Status',
      colQc: 'QC',
      colCompletedBy: 'Completed by',
      colComments: 'Comments',
      colVoidAudit: 'Void / audit',
      colActions: 'Actions',
      empty: 'No log entries for this task.',
      badgeActive: 'active',
      badgeVoided: 'voided',
      qcYes: 'Yes',
      qcNo: 'No',
      auditPending: 'Audit details pending (legacy void).',
      voidReason: 'Reason',
      voidBy: 'Voided by',
      voidAt: 'Voided at',
      actionEdit: 'Edit',
      actionPdf: 'PDF',
      pageSummaryPrefix: 'Page',
      records: 'entries',
      prev: 'Previous',
      next: 'Next',
    },
  },
  compliance: {
    detail: {
      title: 'Compliance Review Detail',
      back_to_dashboard: 'Back to Dashboard',
      export_pdf: 'Export PDF',
      review_section_title: 'Review',
      review_status_label: 'Review Status',
      reviewer_label: 'Reviewed by',
      reviewed_at_label: 'Reviewed at',
      comments_label: 'Comments',
      review_status_placeholder: 'Select a status…',
      update_review_button: 'Save Review',
      task_section_title: 'Task Details',
      task_name_label: 'Task',
      interval_type_label: 'Interval Type',
      view_task_history: 'View Task History',
      no_task: '—',
      corrective_actions_title: 'Corrective Actions',
      no_corrective_actions: 'No corrective actions recorded.',
      discarded_badge: 'Discarded',
      compliance_type_overdue: 'Overdue',
      compliance_type_scheduled: 'Scheduled',
      compliance_type_error: 'Error',
      viewMaintenanceLogs: 'View Maintenance Logs',
      serialNumber: 'Serial Number',
      ellId: 'ELL ID',
    },
    corrective_actions: {
      add_title: 'Add Corrective Action',
      add_placeholder: 'Describe the corrective action taken…',
      edit_placeholder: 'Update the corrective action…',
      add_button: 'Add',
      edit_button: 'Edit',
      discard_button: 'Discard',
      discard_confirm_title: 'Discard corrective action?',
      discard_confirm_description:
        'The corrective action will be marked as discarded and remain visible for audit purposes.',
      discard_confirm_button: 'Discard',
      cancel_button: 'Cancel',
      save_button: 'Save',
      char_count: '{{count}}/5000 characters',
    },
    dashboard: {
      title: 'Compliance Dashboard',
      searchPlaceholder: 'Search by instrument, site, department...',
      filterValueAll: 'All',
      filterValueNonCompliant: 'Non-Compliant',
      filterValuePartial: 'Partial',
      filterValueCompliant: 'Compliant',
      filterStatusPending: 'Pending',
      filterStatusAll: 'All statuses',
      filterStatusComplete: 'Complete',
      bulkActionLabel: 'Set Review Status',
      bulkActionSubmit: 'Update Selected',
      selectedCount: '{{count}} record(s) selected',
      pending: 'Pending',
      noRecords: 'No compliance records found.',
      reviewModalTitle: 'Compliance Review',
      allSites: 'All sites',
      allDepartments: 'All departments',
      allUnits: 'All units',
      filterSearch: 'Search',
      filterSite: 'Site',
      filterDepartment: 'Department',
      filterUnit: 'Unit',
      filterValue: 'Compliance',
      filterStatus: 'NC status',
      dateFrom: 'From date',
      dateTo: 'To date',
      searchButton: 'Search',
      bulkComments: 'Comments (optional)',
      bulkLimitReached:
        'Maximum of {max} items reached. Deselect some or use filters to narrow down.',
      previous: 'Previous',
      next: 'Next',
      paginationPage: 'Page',
      paginationOf: 'of',
      paginationRecords: 'records',
      filterInstruments: 'Instruments',
      allInstruments: 'All instruments',
      serialNumber: 'S/N',
      viewMaintenanceLogs: 'Maintenance Log',
      filterResolutionType: 'Resolution Type',
      allResolutionTypes: 'All resolution types',
      exportCsv: 'Export CSV',
      exportPdf: 'Export PDF',
      whatHappened: {
        missed: 'Missed',
        partial: 'Partial',
        compliant: 'Compliant',
      },
      reviewDecision: {
        overrideOption: 'Override — miss stays on the record',
        confirm: 'Confirm the miss',
        notPerformed: 'Not performed / transferred',
        offline: 'Cleared — offline period',
        pending: 'Pending review',
      },
      resolutionType: {
        confirmTheMiss: 'Confirm the miss',
        override: 'Override',
        notPerformed: 'Not performed / transferred',
        offline: 'Cleared — offline period',
        pending: 'Pending review',
      },
      ncStatus: {
        pending: 'Pending',
        complete: 'Complete',
      },
      table: {
        select: '',
        date: 'Date',
        missedDate: 'Missed Date',
        instrument: 'Instrument',
        serialNumber: 'S/N',
        ellId: 'ELL ID',
        type: 'Type',
        task: 'Task',
        department: 'Department / Unit',
        site: 'Site',
        value: 'Compliance',
        ncStatus: 'NC status',
        whatHappened: 'What happened',
        status: 'Review Status',
        reviewDecision: 'Review decision',
        resolutionType: 'Resolution Type',
        comments: 'Comments',
        maintenanceLog: 'Maint. Log',
        frequency: 'Frequency',
        confirmedBy: 'Confirmed/Ignored By',
        confirmedAt: 'Confirmed/Ignored At',
      },
    },
  },
  profile: {
    pageTitle: 'My Profile',
    editTitle: 'Edit Profile',
    saveButton: 'Save Changes',
    submitting: 'Saving…',
    cancel: 'Cancel',
    cancelDialog: {
      title: 'Discard unsaved changes?',
      description: 'You have unsaved changes. If you leave, your changes will be lost.',
      confirm: 'Discard changes',
      keep: 'Keep editing',
    },
    notificationPreferencesLink: 'Notification Preferences',
    personalInfo: {
      sectionTitle: 'Personal Information',
      firstName: 'First Name',
      firstNamePlaceholder: 'Jane',
      lastName: 'Last Name',
      lastNamePlaceholder: 'Smith',
      middleName: 'Middle Name',
      middleNamePlaceholder: 'Elizabeth',
      email: 'Email',
      emailReadOnly: '(managed by administrator)',
      jobTitle: 'Job Title',
      jobTitlePlaceholder: 'e.g. Lab Technician',
      phone: 'Phone',
      phonePlaceholder: '555 123 4567',
      phoneCode: 'Phone Code',
      employeeId: 'Employee ID',
      employeeIdPlaceholder: 'e.g. EMP-001',
    },
    pin: {
      sectionTitleSet: 'Set PIN',
      sectionTitleChange: 'Change PIN',
      newPin: 'New PIN',
      confirmPin: 'Confirm PIN',
      submitButton: 'Update PIN',
      mismatch: 'PINs do not match.',
      tooShort: 'PIN must be at least 4 digits.',
    },
    accessCategories: {
      sectionTitle: 'Access Categories',
      sectionDescriptionEditable:
        'Control which sites, departments, and units are used to scope your access.',
      sectionDescriptionReadOnly:
        'Sites, departments, and units you have access to. Only an administrator or supervisor can change this.',
      sites: 'Sites',
      departments: 'Departments',
      units: 'Units',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      noneAvailable: 'None available.',
      noneAssigned: 'None assigned.',
      saveButton: 'Save Access',
    },
    permissions: {
      sectionTitle: 'Permissions',
      canViewConfidentialLabel: 'Can view confidential information',
      saveButton: 'Save Permissions',
    },
  },
  notificationPreferences: {
    pageTitle: 'Notification preferences',
    pageSubtitle: 'Set up your notifications in one flow, top to bottom.',

    sitesHeading: 'Sites',
    sitesHelper:
      'Choose which sites these notifications cover. All sites includes any sites added later.',
    allSites: 'All sites',

    departmentsHeading: 'Departments',
    departmentsHelper:
      'You only receive notifications for departments you follow. All departments follows every department you can access.',
    departmentsScopedToSitesHint: 'Options are limited to the sites chosen above.',
    allDepartments: 'All departments',
    departmentsEmpty:
      'You have no department access yet. Ask an administrator to grant department access first.',
    departmentDroppedBySiteHint: 'Removed because its site is no longer selected.',

    eventTypesHeading: 'Event types',
    eventTypesHelper: 'Pick the events you want to be notified about.',

    whenHeading: 'When',
    whenHelper:
      'How often. Immediate fires as events happen. Daily and weekly digests summarize the period and are sent on every channel you select below — email and/or SMS.',
    frequency: {
      immediate: 'Immediate',
      daily: 'Daily digest',
      weekly: 'Weekly digest',
    },

    deliverByHeading: 'Deliver by',
    deliverByHelper: 'Where to send them. Digests use the same channels as immediate alerts.',
    channelEmail: 'Email',
    channelSms: 'SMS',
    digestSmsSupportedNote:
      'Daily and weekly digests will also be sent as SMS text summaries to the phone number on your profile (Immediate SMS is unchanged).',
    mixedDeliveryLoadNote:
      'Your saved settings use different delivery per department. Saving here applies the same delivery to all selected departments.',
    mixedScopeLoadNote:
      'Your saved settings mix account-wide and specific site/department scopes. Saving here keeps the specific scope and drops the account-wide fallback.',

    selectAll: 'Select all',
    selectNone: 'Clear',

    errorNoEventTypes: 'Pick at least one event type.',
    errorNoChannel: 'Choose at least one delivery channel.',
    errorNoFrequency: 'Turn on at least one frequency.',
    errorTooManyRows:
      'This selection expands to too many combinations (max {max}). Narrow the sites, departments, or event types.',

    save: 'Save preferences',
    saving: 'Saving…',
  },
  notificationBell: {
    ariaOpen: 'Open notifications',
    drawerTitle: 'Notifications',
    drawerDescription: 'Your recent alerts',
    markAllRead: 'Mark all as read',
    markingAllRead: 'Marking…',
    empty: 'No notifications yet.',
    previewHint: 'Showing the 3 most recent unread notifications.',
    unreadBadgeAriaLabel: 'unread notifications',
    justNow: 'Just now',
    minutesAgoSuffix: 'm ago',
    hoursAgoSuffix: 'h ago',
    daysAgoSuffix: 'd ago',
    loadMore: 'Load more',
    viewAll: 'View all',
    close: 'Close notifications',
  },
  pagination: {
    page: 'Page',
    of: 'of',
    previous: 'Previous',
    next: 'Next',
  },
  signoffChain: {
    pageTitlePrefix: 'Sign-off chain',
    siteLabel: 'Site',
    emptyState: 'No signers configured. Add a signer below to define the approval sequence.',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove signer',
    selectSignerPlaceholder: 'Select a signer to add…',
    addSigner: 'Add',
    addSignerHint: 'Select a signer and click Add to queue them before saving.',
    addOptional: 'Add Optional',
    or: 'OR',
    save: 'Save chain',
    saving: 'Saving…',
    removeConfirmTitle: 'Remove signer?',
    removeConfirmDescription: 'Are you sure you want to remove this user from the Sign-off Chain?',
    removeConfirmAction: 'Yes, remove',
    confirmTitle: 'Change sign-off sequence?',
    confirmDescription:
      'Changing the sign-off sequence may affect reports currently pending approval. Continue?',
    confirmSave: 'Yes, save changes',
    confirmCancel: 'Cancel',
  },
  impersonationBanner: {
    label: 'Impersonating',
    stopButton: 'Stop Impersonating',
  },
  tenantAdminBanner: {
    label: 'Viewing as admin in',
    exitButton: 'Exit',
  },
  reports: {
    validation: {
      endBeforeStart: 'The end date cannot be earlier than the start date.',
    },
    monthly: {
      title: 'Monthly Reports',
      filtersTitle: 'Filters',
      filtersClear: 'Clear',
      filtersDone: 'Done',
      filterSite: 'Site',
      filterDepartment: 'Department',
      filterMonthFrom: 'Month From',
      filterMonthTo: 'Month To',
      showAll: 'Show All Departments',
      myQueue: 'My queue',
      allSites: 'All sites',
      allDepartments: 'All departments',
      viewReport: 'View Report',
      signed: 'Signed',
      statusOpen: 'Open',
      statusPending: 'Pending sign-off',
      statusSigned: 'Completed',
      noReports: 'No reports found for the selected period.',
      paginationRecords: 'reports',
      compliancePercent: 'Compliance',
      lastModified: 'Last modified',
      signatures: 'Signatures',
      downloadPdf: 'Download PDF',
      downloadPdfFull: 'Download PDF (Full)',
      generatePdf: 'Generate PDF',
      pdfGenerating: 'PDF is being generated, check back shortly.',
      exportXlsx: 'Export XLSX',
      pdf: {
        generating: 'PDF is being generated, check back shortly.',
        notReady: 'PDF not yet generated.',
      },
      exports: {
        generatingInProgress: 'Generating {format}…',
        failed: 'Export failed. Please try again.',
        generatePdf: 'Generate PDF',
        generateXlsx: 'Generate XLSX',
        downloadPdf: 'Download PDF',
        downloadXlsx: 'Download XLSX',
        downloadPdfFull: 'PDF (Full)',
        download: 'Download',
        pdf: 'PDF',
        excel: 'Excel',
      },
      reportDetail: 'Report Detail',
      detailInstrument: 'Instrument',
      detailTask: 'Task',
      detailType: 'Type',
      detailUnit: 'Unit',
      detailCompliance: 'Compliance %',
      detailConfirmed: 'Confirmed',
      detailOverrides: 'Overrides',
      detailLevel: 'Level',
      noDetailRows: 'No instrument/task records for this period.',
      reportNotCreated: 'Report not Created',
      missingReportPendingLabel: 'Pending',
      derivedStatusInProgress: 'Not ready (month in progress)',
      derivedStatusActionNeeded: 'Action needed',
      derivedStatusReadyToSign: 'Ready to sign',
      liveIndicatorLabel: 'Live',
      liveIndicatorDescription: 'reflects current data',
      statusRejected: 'Rejected / Re-open',
      summary: 'Summary — 3-Month Compliance & QA Alerts',
      trends: {
        title: 'Compliance Trends',
        currentMonth: 'Current Month',
        last3Avg: 'Last 3-Month Avg',
        last6Avg: 'Last 6-Month Avg',
        monthlyBarsTitle: '3-Month Overview',
        threeMonthCompliance: '3-Month Compliance',
      },
      alerts: {
        title: 'QA Alerts',
        titleForPeriod: 'QA Alerts — {period}',
        errors: 'Errors',
        failures: 'Failures',
        nonCompliant: 'Non-Compliant Events',
        serviceReports: 'Service Reports',
        dateChanges: 'Date Changes',
        correctiveActions: 'Corrective Actions',
        statusChanges: 'Status Changes',
        departmentMoved: 'Department Moved',
      },
      license: {
        title: 'License Summary',
        noFields: 'No licence fields on record.',
        licenseNumber: 'License Number (CLIA)',
        jointCommission: 'Joint Commission',
        cap: 'CAP',
        cola: 'COLA',
        aabb: 'AABB',
        aala: 'AALA',
        aoa: 'AOA',
        ashi: 'ASHI',
        nyDoh: 'NY DOH',
        caDoh: 'CA DOH',
        flDoh: 'FL DOH',
        nySpfi: 'NY SPFI',
        fda: 'FDA',
        auId: 'AU ID',
      },
      unitsSummary: {
        title: 'Instruments',
        byUnit: 'by Unit',
        titleWithCounts: 'Instruments ({instrumentCount}) by Unit ({unitCount})',
        instrumentCount: 'Instruments: {count}',
        colInstrument: 'Instrument',
        colSerial: 'Serial Number',
        colCompliance: 'Compliance %',
        noData: 'No data',
        complianceBadge: 'Compliance [{percent}%]',
      },
      unitList: {
        title: 'Unit List',
        colUnit: 'Unit',
        colCompliance: 'Compliance %',
        colInstrument: 'Instrument',
        noInstruments: 'No instruments for this period.',
      },
      correctiveActions: {
        title: 'Corrective Actions',
        noItems: 'No corrective actions recorded.',
        add: 'Add Corrective Action',
        edit: 'Edit',
        delete: 'Delete',
        save: 'Save',
        cancel: 'Cancel',
        commentLabel: 'Comment',
        createdBy: 'Created by',
        confirmDeleteTitle: 'Delete Corrective Action',
        confirmDelete: 'Are you sure you want to delete this corrective action?',
      },
      attachments: {
        title: 'Attachments',
        noItems: 'No attachments uploaded.',
        upload: 'Upload File',
        download: 'Download',
        delete: 'Delete',
        cancel: 'Cancel',
        descriptionLabel: 'Description (optional)',
        uploadedBy: 'Uploaded by',
        confirmDeleteTitle: 'Delete Attachment',
        confirmDelete: 'Are you sure you want to delete this attachment?',
        allowedFormats:
          'Allowed formats: JPEG, PNG, GIF, TIFF, PDF, Word, Excel, PowerPoint, RTF, TXT, CSV, ZIP',
        selectFile: 'Choose File',
        changeFile: 'Change',
        uploading: 'Uploading...',
      },
      maintenanceLogs: {
        title: 'Maintenance Logs',
        noInstruments: 'No instruments found for this report period.',
        prevInstrument: 'Previous instrument',
        nextInstrument: 'Next instrument',
        offlineBadge: 'Offline',
        alertTrendTitle: '3-Month Alert Trend',
        failures: 'Failures',
        nonCompliance: 'Non-Compliance',
        serviceReports: 'Service Reports',
        dateChanges: 'Date Changes',
        activeLots: 'Active Supplies',
        loading: 'Loading...',
        hideEmptyData: 'Hide instruments with no logged activity',
      },
      ncSymbolLegend: {
        title: 'Symbol Guide',
        labelComplete: 'Complete',
        labelCompleteDateChanged: completeDateChangedLabel,
        labelFailure: ncActionLabels.unreviewed,
        labelNoTesting: ncActionLabels.noTesting,
        labelOverride: ncActionLabels.override,
        labelConfirmed: ncActionLabels.confirmed,
        complete: 'Complete — task performed successfully',
        completeDateChanged: `${completeDateChangedLabel} — task completed with a modified date`,
        failure: 'Non-Compliance (Unreviewed) — missed occurrence not yet reviewed',
        noTesting: 'No Testing — intentionally skipped (not counted as non-compliant)',
        override: 'Override the Miss — deviation reviewed and accepted by user',
        confirmed: 'Confirmed Non-Compliance — unresolved missed occurrence',
        dayOff: 'Day Off — instrument not scheduled on this day',
      },
      users: {
        title: 'Users Activity',
        exportPdf: 'Export PDF',
        searchPlaceholder: 'Search users...',
        colName: 'Display Name',
        colTasksCompleted: 'Tasks Completed',
        colLateLogs: 'Late Logs',
        colAvgCompliance: 'Avg Compliance %',
        noUsers: 'No user activity found for this report period.',
        pdfPeriod: 'Period',
        pdfDepartment: 'Department',
      },
    },
    deleted_history: {
      title: 'Deleted Records History',
      filterSearch: 'Search (instrument name)',
      filterSearchPlaceholder: 'Search…',
      filterSite: 'Site',
      filterSitePlaceholder: 'All sites',
      filterDepartment: 'Department',
      filterDepartmentPlaceholder: 'All departments',
      filterUnit: 'Unit',
      filterUnitPlaceholder: 'All units',
      filterInstrument: 'Instrument',
      filterInstrumentPlaceholder: 'All instruments',
      filterType: 'Type',
      filterTypePlaceholder: 'All types',
      fromDate: 'From Date',
      toDate: 'To Date',
      allSites: 'All sites',
      allDepartments: 'All departments',
      filters: 'Filters',
      clearFilters: 'Clear',
      exportCsv: 'Export CSV',
      exportPdf: 'Export PDF',
      export: {
        filterRequired: 'Apply a date filter to export a report.',
      },
      loadMore: 'Load More',
      noRecords: 'No deleted records found for the selected filters.',
      colType: 'Type',
      colInstrument: 'Instrument',
      colSerial: 'Serial #',
      colTask: 'Task',
      colTaskCompleted: 'Task Completed',
      colSite: 'Site',
      colMaintenanceLogs: 'Maintenance Logs',
      colDepartment: 'Department',
      colDeletedBy: 'Deleted By',
      colDeletedAt: 'Deleted Date',
      colReason: 'Reason',
      recordType_instrument: 'Instrument',
      recordType_task: 'Task',
      recordType_task_log: 'Task Log',
      recordType_unknown: 'Unknown',
    },
    sites: {
      title: 'Sites Report',
      fromDate: 'From Date',
      toDate: 'To Date',
      filters: 'Filters',
      clearFilters: 'Clear',
      // Guard canaries (e58-s1): labels for Site/Dept/Unit chrome — must stay unused on this page.
      filterSite: 'Site',
      filterDepartment: 'Department',
      filterUnit: 'Unit',
      filterSitePlaceholder: 'All sites',
      allDepartments: 'All departments',
      allUnits: 'All units',
      exportCsv: 'Export CSV',
      exportPdf: 'Export PDF',
      noRecords: 'No sites found for the selected filters.',
      colSite: 'Site',
      colDepartments: 'Departments',
      colInstruments: 'Instruments',
      colAvgCompliance: 'Compliance %',
      colFailures: 'Failures',
      colNonComplianceEvents: 'Non-Compliant Events',
      colServiceReports: 'Service Reports',
      colDateChanges: 'Date Changes',
      paginationNoun: 'sites',
    },
    departments: {
      title: 'Departments Report',
      fromDate: 'From Date',
      toDate: 'To Date',
      filters: 'Filters',
      clearFilters: 'Clear',
      // Guard canaries (e58-s1): labels for Site/Dept/Unit chrome — must stay unused on this page.
      filterSite: 'Site',
      filterDepartment: 'Department',
      filterUnit: 'Unit',
      filterSitePlaceholder: 'All sites',
      allDepartments: 'All departments',
      allUnits: 'All units',
      exportCsv: 'Export CSV',
      exportPdf: 'Export PDF',
      noRecords: 'No departments found for the selected filters.',
      colDepartment: 'Department',
      colSite: 'Site',
      colInstruments: 'Instruments',
      colAvgCompliance: 'Compliance %',
      paginationNoun: 'departments',
    },
    units: {
      title: 'Units Report',
      hideEmpty: 'Hide Empty Data',
      fromDate: 'From Date',
      toDate: 'To Date',
      filters: 'Filters',
      clearFilters: 'Clear',
      // Guard canaries (e58-s1): labels for Site/Dept/Unit chrome — must stay unused on this page.
      filterSite: 'Site',
      filterDepartment: 'Department',
      filterUnit: 'Unit',
      filterSitePlaceholder: 'All sites',
      allDepartments: 'All departments',
      allUnits: 'All units',
      exportCsv: 'Export CSV',
      exportPdf: 'Export PDF',
      noRecords: 'No units found for the selected filters.',
      colUnit: 'Unit',
      colDepartment: 'Department',
      colSite: 'Site',
      colInstruments: 'Instruments',
      colAvgCompliance: 'Compliance %',
      paginationNoun: 'units',
      colFailures: 'Failures',
      colNonComplianceEvents: 'Non-Compliant Events',
      colServiceReports: 'Service Reports',
      colDateChanges: 'Date Changes',
      colInstrument: 'Instrument',
      colSerialNumber: 'Serial No.',
      colRetired: 'Retired',
      expandUnit: 'Expand unit',
      colCompliance: 'Compliance',
    },
    instruments: {
      title: 'Instruments Report',
      filterSite: 'Site',
      filterSitePlaceholder: 'All sites',
      filterDepartment: 'Department',
      filterDepartmentPlaceholder: 'All departments',
      filterInstrumentType: 'Instrument Type',
      filterInstrumentTypePlaceholder: 'All types',
      hideEmpty: 'Hide Empty Data',
      fromDate: 'From Date',
      toDate: 'To Date',
      filters: 'Filters',
      clearFilters: 'Clear',
      exportCsv: 'Export CSV',
      exportPdf: 'Export PDF',
      noRecords: 'No instruments found for the selected filters.',
      colInstrument: 'Instrument',
      colSerial: 'Serial No.',
      colUnit: 'Unit',
      colDepartment: 'Department',
      colSite: 'Site',
      colAvgCompliance: 'Compliance %',
      paginationNoun: 'instruments',
      typeGroupCount: '{count} instruments',
      typeGroupUnknown: 'No Type',
      expandType: 'Expand instrument type',
    },
    users: {
      title: 'Users Activity Report',
      searchLabel: 'Search name or email',
      searchPlaceholder: 'Search…',
      fromDate: 'From Date',
      toDate: 'To Date',
      filters: 'Filters',
      clearFilters: 'Clear',
      exportCsv: 'Export CSV',
      exportPdf: 'Export PDF',
      noRecords: 'No users found for the selected filters.',
      noLoggers: 'No loggers for this report.',
      loading: 'Loading…',
      filterSite: 'Sites',
      filterSitePlaceholder: 'All sites',
      activeUsersTally: '{count} users with logged activity',
      sectionUsers: 'System Users ({count})',
      sectionLoggers: 'Loggers ({count})',
      colUser: 'User',
      colEmail: 'Email',
      colSites: 'Sites',
      colDepartments: 'Departments',
      colTasksCompleted: 'Tasks completed',
      paginationNoun: 'users',
    },
    maintenanceLogs: {
      title: 'Maintenance Logs Report',
      filterSite: 'Site',
      filterDepartment: 'Department',
      filterUnit: 'Unit',
      filterInstrument: 'Instrument',
      filterInstrumentTypes: 'Instrument types',
      allSites: 'All sites',
      allDepartments: 'All departments',
      allUnits: 'All units',
      allInstruments: 'All instruments',
      allInstrumentTypes: 'All instrument types',
      hideEmptyData: 'Hide Empty Data',
      filters: 'Filters',
      clearFilters: 'Clear',
      exportPdf: 'Export PDF',
      exportCsv: 'Export Excel',
      filtersOrganizationSection: 'Report scope',
      noInstruments: 'No instruments match the selected filters.',
      selectInstrument: 'Instrument',
      selectInstrumentPlaceholder: 'Choose an instrument…',
      summaryCompliance: 'Average compliance (detail rows in range)',
      complianceBadge: 'Compliance',
      calendarTitle: 'Calendar View',
      kpiBarLabel: 'KPI summary',
      lotSummary: {
        title: 'Supplies Summary',
        empty: 'No Supplies Present.',
        lot: 'Supply',
        lotNumber: 'Lot Number',
        manufacturer: 'Manufacturer',
        product: 'Product #',
        expires: 'Expiration Date',
        viewDetails: 'View Details',
        controlsReagents: 'Supplies',
        dateReceived: 'Date Received',
        dateOpened: 'Date Opened',
        openedExpiration: 'Opened Expiration',
        dateInUse: 'Date in Use',
        dateDiscarded: 'Date Discarded',
        otherIdentifier: 'Other Identifier',
        description: 'Description',
      },
      monthLabel: 'Month',
      prevInstrument: 'Prev Instrument',
      nextInstrument: 'Next Instrument',
      instrumentPosition: 'Instrument {current} of {total}',
      filterTask: 'Task',
      jumpToPage: 'Go to #',
      jumpToPagePlaceholder: '#',
      // Retained for regression tests that assert the peer Task Log History section is gone.
      logsTitle: 'Task log history',
      // Column labels kept for E52-S2 row completeness (dept/unit/completed-at).
      colLoggedAt: 'Logged at',
      colTask: 'Task',
      colStatus: 'Status',
      colCompletedAt: 'Completed at',
      colCompletedBy: 'Completed by',
      colDepartment: 'Department',
      colUnit: 'Unit',
      colDeptUnit: 'Dept / Unit',
      colComments: 'Comments',
      activityFeed: {
        title: 'History',
        viewInstrument: 'View Instrument',
        empty: 'No history events in the selected date range.',
        showMore: 'Show {count} more',
        showLess: 'Show less',
        backdatedActionAt: 'Performed:',
        // Field label for mod_reason detail only — not a status chip (E52-S2 / AC2).
        // Legacy wording, shared with the Reports list and Log Details (REBUILD-607).
        dateModifiedReason: 'Date Modified Reason:',
        // Legacy label for the NC-confirmation / override comment on a review row (REBUILD-622).
        explanation: 'Explanation:',
        acceptable: '(acceptable)',
        unacceptable: '(unacceptable)',
        eventTypes: {
          task_log: 'Task Log',
          service_report: 'Service Report',
          status_change: 'Status Change',
          department_move: 'Department Move',
          review_action: 'NC Review',
          backdated: 'Back Dated',
          task_deactivation: 'Frequency Change',
        },
        statusChangeDialog: {
          title: 'Status Change',
          oldStatus: 'Previous Status',
          newStatus: 'New Status',
          changedBy: 'Changed By',
          changedAt: 'Changed At',
          comments: 'Comments',
        },
        departmentMoveDialog: {
          title: 'Department Move',
          from: 'From',
          to: 'To',
          changedBy: 'Changed By',
          changedAt: 'Changed At',
          comments: 'Comments',
        },
        deactivationDialog: {
          title: 'Frequency Change',
          taskName: 'Task',
          oldFrequency: 'Previous Frequency',
          newFrequency: 'New Frequency',
          performedBy: 'Changed By',
          performedAt: 'Changed At',
        },
        reviewActionDialog: {
          title: 'NC Review',
          taskName: 'Task',
          status: 'Status',
          missedAt: 'Scheduled / Missed at',
          reviewedBy: 'Reviewed By',
          reviewedAt: 'Reviewed At',
          comments: 'Comments',
        },
      },
    },
    signatures: {
      title: 'Pending Signatures',
      noPending: 'No reports are pending your signature.',
      sign: 'Sign',
      signNow: 'Sign Now',
      reject: 'Reject',
      cancel: 'Cancel',
      rejectReason: 'Rejection Reason',
      rejectReasonPlaceholder: 'Enter reason for rejection...',
      rejectReasonRequired: 'A rejection reason is required.',
      success: 'Report signed successfully.',
      rejectSuccess: 'Report rejected.',
      confirmSign: 'Are you sure you want to sign this report?',
      confirmSignTitle: 'Sign Report',
      chainStatus: 'Sign-off Chain',
      undo: 'Undo Signature',
      undoSuccess: 'Signature undone.',
      pinLabel: 'PIN',
      pinPlaceholder: 'Enter your PIN',
      commentOptional: 'Add a comment (optional)',
      rejectionAlertTitle: 'Report Rejected',
      rejectionAlertDismiss: 'Dismiss',
      sectionTitle: 'Signatures',
      historyTitle: 'Audit Trail',
      historyEmpty: 'No signature activity recorded.',
      signed: 'Signed',
      rejected: 'Rejected (sent back)',
      undone: 'Undone',
      invalidated: 'Invalidated by rejection',
      invalidPin: 'Invalid PIN. Please try again.',
      createPinTitle: 'Set up your signing PIN',
      createPinPrompt:
        "You need a PIN to sign reports. Create one now to continue — you won't leave this report.",
      createPinButton: 'Create PIN',
      newPinLabel: 'New PIN',
      confirmPinLabel: 'Confirm PIN',
      pinHint: 'Must be at least 4 digits.',
      pinMismatch: 'PINs do not match.',
      pinTooShort: 'PIN must be at least 4 digits.',
      pinCreatedPrompt: '✓ PIN created. Enter it below to sign this report.',
    },
  },
  approvals: {
    breadcrumb: 'Signatures',
    title: 'Signatures',
    noPending: 'No Signatures at this time.',
    viewNow: 'View Now',
    chainStatus: 'Sign-off Chain',
    rejectionAlertTitle: 'Report Rejected',
    rejectionAlertDismiss: 'Dismiss',
  },
  nonCompliance: {
    dashboard: {
      title: 'Non-Compliance',
      empty: 'No Non-Compliance at this time.',
      loading: 'Loading, please wait...',
      paginationNoun: 'records',
      oldestNcLabel: 'Oldest open NC:',
      resolveMisses: 'Resolve misses',
      sortNewest: 'Newest first',
      sortOldest: 'Oldest first',
      ageDays: '{days}d ago',
    },
    card: {
      heading: 'Non Compliance Review',
      viewInstrument: 'View Instrument',
      viewMaintenanceLogs: 'View Maintenance Logs',
      serialNumber: 'S/N',
      ellId: 'ELL ID',
      location: 'Location',
      taskLabel: 'Task',
      missedDate: 'Missed Date',
      missedTime: 'Missed Time',
      frequency: 'Frequency',
      calendarFrequency: 'Calendar Frequency',
      startTime: 'Start Time',
    },
    actions: {
      resolve: {
        title: 'Resolve',
        subtitle: ncActionLabels.resolve,
      },
      npt: {
        title: ncActionLabels.noTesting,
        subtitle: ncActionLabels.noTesting,
      },
      ignore: {
        title: 'Override',
        subtitle: ncActionLabels.override,
      },
      confirm: {
        title: 'Confirm',
        subtitle: ncActionLabels.confirmFull,
      },
      offlineRetire: {
        title: ncActionLabels.offlineRetire,
        subtitle: 'Instrument was offline',
      },
    },
    dialogs: {
      commentsLabel: 'Comments',
      commentsRequired: 'Required.',
      commentsTooLong: 'Too long (max {max}).',
      resolve: {
        heading: ncActionLabels.resolve,
        fillTheHole:
          'To Resolve the Miss, Please Review the Date and Time Below and Mark as Complete.',
        dateModNote: 'PLEASE NOTE: This will show as a Date Modification in the Log.',
        taskNotes: 'Task Notes',
        completedBy: 'Completed By',
        completedAt: 'Date and Time Completed',
        reasonLabel: 'Reason (required)',
        reasonPlaceholder: 'Enter reason for late entry',
        submit: 'Mark as Complete',
        cancel: 'Cancel',
        outOfWindow:
          'the time must be within the overdue window; the date and time must be prior to or equal to the missed date and time',
        outOfWindowBefore:
          'the time must be within the allowed window; the date and time cannot be before the window opens',
      },
      ignore: {
        heading: ncActionLabels.override,
        warningPrimary:
          'You can override the miss: it will NOT count against your compliance percentages.',
        commentsPlaceholder: 'Enter explanation',
        submit: ncActionLabels.override,
        cancel: 'Cancel',
      },
      confirm: {
        heading: ncActionLabels.confirmFull,
        warningPrimary: 'This will count against your Compliance Percentages!',
        warningSecondary:
          'This should only be submitted if the task was missing and no corrections is possible. To correct the entry instead, Cancel and use either Resolve the Miss or No Testing functionality.',
        commentsPlaceholder: 'Enter explanation',
        submit: ncActionLabels.confirmFull,
        cancel: 'Cancel',
      },
      offlineRetire: {
        heading: ncActionLabels.offlineRetire,
        warningPrimary:
          'This miss falls entirely inside a closed Offline, Down for Maintenance, or Retired period.',
        warningSecondary:
          'Clear it without recording a completion inside that period. A comment is required for the audit trail.',
        commentsPlaceholder: 'Enter explanation',
        submit: ncActionLabels.offlineRetire,
        cancel: 'Cancel',
      },
    },
    timeline: {
      heading: 'Timeline',
    },
    flash: {
      reviewSaved: 'Review saved.',
      resolveSaved: 'Task log created. The miss has been resolved.',
      bulkReviewSaved: '{count} item(s) updated successfully.',
    },
    errors: {
      reviewFailed: 'Review action failed. Please try again.',
    },
    filters: {
      filterSearch: 'Search',
      filterSite: 'Site',
      filterDepartment: 'Department',
      filterUnit: 'Unit',
      filterDateFrom: 'From Date',
      filterDateTo: 'To Date',
      allSites: 'Sites',
      allDepartments: 'Departments',
      allUnits: 'Units',
      searchSites: 'Search sites...',
      searchDepartments: 'Search departments...',
      searchUnits: 'Search units...',
      searchPlaceholder: 'Search instrument, site, department…',
      dateFrom: 'From date',
      dateTo: 'To date',
      searchButton: 'Filters',
      clear: 'Clear',
      done: 'Done',
    },
    bulk: {
      selectMultiple: 'Select Multiple',
      selectAll: 'Select All',
      selected: 'Selected Items',
      selectedHint: '(Select Multiple Items below to Change)',
      cancel: 'Cancel',
      override: ncActionLabels.override,
      noTesting: ncActionLabels.noTesting,
      confirmNonCompliance: ncActionLabels.confirmFull,
      warning: 'Warning: This action will affect all the items currently selected',
      commentsPlaceholder: 'Add an Explanation',
      commentsRequiredForAction: 'An explanation is required for this action.',
      submitting: 'Submitting…',
      limitReached: 'Maximum of {max} items reached. Deselect some or use filters to narrow down.',
      selectAllCapped: 'First {max} of {total} items selected. Use filters to narrow results.',
      confirm: {
        titleOverride: 'Override the Miss for {count} item(s)?',
        titleNoTesting: 'Apply No Testing to {count} item(s)?',
        titleConfirmNonCompliance: 'Confirm Non-Compliant Event for {count} item(s)?',
        body: 'This action cannot be undone. All selected items will be updated.',
        explanationLabel: 'Explanation',
        noExplanation: 'No explanation provided',
        confirmButton: 'Confirm',
        cancelButton: 'Cancel',
      },
      errors: {
        noSelection: 'At least one item must be selected.',
        tooMany: 'Cannot select more than {max} items at once.',
        fetchFailed: 'Could not load selectable items. Please try again.',
      },
    },
  },
  barcodeScanner: {
    title: 'Scan Barcode',
    scanNow: 'Scan the instrument barcode with your scanner now...',
    scanning: 'Scanning... ({count} characters)',
    pointCamera:
      'Use your device camera to scan the instrument barcode. Keep the entire barcode in frame.',
    noCamera: 'No camera found. If you have a barcode scanner connected, scan now.',
    scanButton: 'Scan Barcode',
    notFound: 'No instrument was found with that barcode.',
    multipleFound: 'More than one instrument was found with that barcode.',
    lookupError: 'Error looking up barcode. Please try again.',
  },
  schedule: {
    loggingSchedule: 'Logging Schedule',
    followSystemSchedule: 'Follow System Schedule',
    followSiteSchedule: 'Follow Site Schedule',
    followUnitSchedule: 'Follow Unit Schedule',
    dayOn: 'On',
    dayOff: 'Off',
    parentPreviewHint: 'Following parent schedule — shown for reference',
    parentPreviewLoading: 'Loading parent schedule…',
    parentPreviewUnavailable: 'Could not load parent schedule.',
    parentPreviewRetry: 'Try again',
    days: {
      sun: 'Su',
      mon: 'Mo',
      tue: 'Tu',
      wed: 'We',
      thu: 'Th',
      fri: 'Fr',
      sat: 'Sa',
    },
    errors: {
      atLeastOneDay: 'At least one working day must be selected.',
    },
  },
  taskQueue: {
    dashboard: {
      title: 'Task Queue',
      paginationNoun: 'instruments',
      allUpToDate: 'All tasks are up to date.',
      noDataWithFilters: 'No data found, try with different filters.',
      noNonCompliance: 'No non-compliance items found.',
      loading: 'Loading, please wait...',
    },
    toggle: {
      showNonCompliance: 'Show Non-Compliance',
      hideNonCompliance: 'Hide Non-Compliance',
      appliedToAllInstruments: 'Applied to all instruments',
      ncOpenCountDescription: '{count} open non-compliance item(s)',
    },
    calendar: {
      show: 'Show Calendar',
      hide: 'Hide Calendar',
    },
    sections: {
      overdue: 'Overdue',
      dueNow: 'Due Now',
      ready: 'Ready',
      nonCompliance: 'Non Compliance Review',
      noNonCompliance: 'No non-compliance records for this instrument.',
    },
    card: {
      serialNumber: 'S/N',
      viewInstrument: 'View Instrument',
      viewLogs: 'View Maintenance Logs',
      logTask: 'Log Task',
      startTime: 'Start Time',
      ellId: 'ELL ID:',
      location: 'Location',
      lastPerformed: 'Last Performed:',
      viewDetails: 'View Details',
      logDetailsTitle: 'Log Details',
      noLogEntries: 'No log entries for this task yet.',
      close: 'Close',
      instrumentType: 'Type',
      selectSupply: 'Select Supply on Instrument →',
    },
    filters: {
      searchPlaceholder: 'Search...',
      allInstruments: 'Instruments',
      instrumentsAsSearch: 'Search',
      searchInstruments: 'Search instruments...',
      allSites: 'Sites',
      allDepartments: 'Departments',
      allUnits: 'Units',
      allTags: 'Tags',
      searchTags: 'Search tags...',
      searchSites: 'Search sites...',
      searchDepartments: 'Search departments...',
      searchUnits: 'Search units...',
      searchButton: 'Filters',
      apply: 'Apply',
      cancel: 'Cancel',
      reset: 'Clear',
      clear: 'Clear',
      done: 'Done',
      noTagsFound: 'No tags found',
      noResults: 'No results found',
      filterSearch: 'Search',
      filterSite: 'Site',
      filterDepartment: 'Department',
      filterUnit: 'Unit',
      filterTags: 'Tags',
      filterInstruments: 'Instruments',
      hideAllNonCompliance: 'Hide all non-compliance',
      hideAllNonComplianceHint: 'Showing only Overdue, Due Now, and Ready tasks',
    },
    overflow: {
      showMore: 'Show {count} more tasks',
      loading: 'Loading...',
    },
  },
  contactUs: {
    pageTitle: 'Contact Us',
    callUs: {
      heading: 'Call Us',
      phone: '(844) LAB-LOGS',
      phoneAlt: '(844) 522-5647',
      hours: '07:00 – 19:00 Eastern Timezone',
    },
    writeUs: {
      heading: 'Write Us',
      email: 'support@lablogs.com',
    },
    needHelp: {
      heading: 'Need Help?',
      instruction: 'Select the Bottom Right Corner Chat Icon',
    },
  },
  shared: {
    dateTimePicker: {
      now: 'Now',
    },
    richTextEditor: {
      uploading: 'Uploading image…',
      uploadFailed: 'Image upload failed. Please try again.',
      noColor: 'No color',
    },
    multiSelect: {
      selectAll: 'Select All',
      clearFilter: 'Clear filter',
      removeTag: 'Remove {label}',
      selectedCount: '{count} selected',
      hiddenTagCount: '+{count}',
      noResults: 'No results found.',
    },
    taskFilter: {
      allTasks: 'All tasks',
      showAll: 'Show All',
      selectAll: 'Select All',
      deselectAll: 'Deselect All',
      selectedCount: '{count} selected',
      searchPlaceholder: 'Search tasks…',
      searchAriaLabel: 'Search tasks',
    },
    searchableSelect: {
      searchPlaceholder: 'Search...',
      emptyMessage: 'No results found.',
    },
  },
} as const
